/**
 * Central place for API calls to the Nema Care backend (uses fetch).
 * Set REACT_APP_API_URL in .env (e.g. http://localhost:5000) if the default differs.
 */
import { GST_RATE, PAYMENT_AMOUNTS } from "../config/nemaFeeSchedule";
import { ROLE_SIDEBAR_CONFIG } from "../components/shared/roleSidebarConfig";
import { loginPathForContext } from "../utils/routeAccess";
import { formatBillingLineCode, formatSubscriptionStatus } from "../utils/displayLabels";
import { resolveMessageFromApiBody } from "../utils/userFacingErrors";

const configuredApiUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
/** In local dev, same-origin requests via package.json `proxy` avoid flaky cross-port fetches. */
const API_BASE_URL =
  process.env.NODE_ENV === "development" &&
  (!configuredApiUrl || /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(configuredApiUrl))
    ? ""
    : configuredApiUrl || "http://localhost:5000";
const IS_PRODUCTION = process.env.NODE_ENV === "production";
const TOKEN_STORAGE_KEY = "nema_auth_token";
const CUSTOMER_PROFILE_STORAGE_KEY = "nema_customer_profile_v1";
const CUSTOMER_PROFILE_UPDATED_EVENT = "nema:customer-profile-updated";
const CAREGIVER_PROFILE_UPDATED_EVENT = "nema:caregiver-profile-updated";
const CUSTOMER_SUBSCRIPTION_UPDATED_EVENT = "nema:customer-subscription-updated";
const HOURLY_WALLET_STORAGE_KEY = "nema_hourly_wallet_v1";
const CUSTOMER_ADD_ONS_STORAGE_KEY = "nema_customer_add_ons_v1";
const CAREGIVER_ACCESS_STATUS_STORAGE_KEY = "nema_caregiver_access_status_v1";
const DEVICE_FINGERPRINT_STORAGE_KEY = "nema_device_fingerprint_v1";
const DEVICE_LOGIN_AUDIT_STORAGE_KEY = "nema_device_login_audit_v1";
const DPDP_CONSENT_STORAGE_KEY = "nema_dpdp_consent_v1";
const OPS_ASSESSMENTS_STORAGE_KEY = "nema_ops_assessments_v1";
const CARE_MANAGER_SUBSCRIPTION_STORAGE_KEY = "nema_care_manager_subscription_v1";
const CAREGIVER_BILLING_STORAGE_KEY = "nema_caregiver_billing_v1";
const CAREGIVER_SIDEBAR_USER_KEY = "nema_caregiver_sidebar_user_v1";
const CAREGIVER_COMPENSATION_STORAGE_KEY = "nema_caregiver_compensation_v1";

/**
 * Every localStorage key that holds data scoped to the signed-in account.
 * Cleared in full on logout so a new login on the same browser/device never
 * inherits the previous user's booking/session state. Device-level keys
 * (fingerprint, FCM push token, caregiver login-mobile prefill, voice
 * guidance prefs) are intentionally NOT in this list — they identify the
 * device/browser, not the account, and clearing them would defeat their
 * purpose or degrade UX for the next legitimate login on the same device.
 */
const USER_SCOPED_LOCAL_STORAGE_KEYS = [
  TOKEN_STORAGE_KEY,
  CUSTOMER_PROFILE_STORAGE_KEY,
  HOURLY_WALLET_STORAGE_KEY,
  CUSTOMER_ADD_ONS_STORAGE_KEY,
  CAREGIVER_ACCESS_STATUS_STORAGE_KEY,
  DEVICE_LOGIN_AUDIT_STORAGE_KEY,
  DPDP_CONSENT_STORAGE_KEY,
  OPS_ASSESSMENTS_STORAGE_KEY,
  CARE_MANAGER_SUBSCRIPTION_STORAGE_KEY,
  CAREGIVER_BILLING_STORAGE_KEY,
  CAREGIVER_SIDEBAR_USER_KEY,
  CAREGIVER_COMPENSATION_STORAGE_KEY,
  "nema_marketplace_browse_guard_v1",
  "nema_caregiver_service_role",
  "nema_caregiver_onboarding_v1",
  "nema_emergency_pool_available_ids_v1",
];

/**
 * Wipe every user-scoped localStorage key plus the whole of sessionStorage
 * (which in this app holds only per-visit booking/UI state — care pass
 * checkout session, booking draft, search resume, caregiver preferences,
 * messenger call state — never shared/global config).
 */
function clearUserScopedStorage() {
  try {
    USER_SCOPED_LOCAL_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
  try {
    window.sessionStorage.clear();
  } catch {
    /* ignore */
  }
}

const AUTH_EXEMPT_PATHS = [
  "/api/auth/login",
  "/api/auth/login/otp",
  "/api/auth/login/otp/request",
  "/api/auth/otp/send",
  "/api/auth/otp/verify",
  "/api/auth/register",
  "/api/auth/user/login",
  "/api/caregiver/onboarding/register",
  "/api/care-manager/onboarding/register",
  "/api/marketplace/public/",
  "/api/marketplace/carehub/catalog",
  "/api/marketplace/carehub/agreement-reference",
  "/api/public/",
  "/api/platform/config",
  "/api/notifications/push-config",
  "/api/marketplace/carehub/estimate",
  "/api/availability/catalog",
  "/api/availability/slots",
  "/api/availability/matchmaker",
  "/api/availability/calendar",
];

export const PAYMENT_TYPES = {
  SUBSCRIPTION: "SUBSCRIPTION",
  DEPOSIT: "DEPOSIT",
  COMMITMENT_FEE: "COMMITMENT_FEE",
  ACTIVATION_FEE: "ACTIVATION_FEE",
  POLICE_VERIFICATION: "POLICE_VERIFICATION",
  CARE_MANAGER_FEE: "CARE_MANAGER_FEE",
  REACTIVATION_FEE: "REACTIVATION_FEE",
  CARE_MANAGER_SUBSCRIPTION: "CARE_MANAGER_SUBSCRIPTION",
  CAREGIVER_REACTIVATION_FEE: "CAREGIVER_REACTIVATION_FEE",
};

export { PAYMENT_AMOUNTS };

async function unregisterPushBeforeLogout() {
  const jwt = window.localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!jwt) return;
  try {
    const m = await import("./pushNotifications");
    const token = m.getLastFcmToken?.();
    if (token) {
      try {
        await request("/api/notifications/device-token", {
          method: "DELETE",
          body: JSON.stringify({ token }),
        });
      } catch {
        // ignore logout unregister errors
      }
    }
    m.resetPushRegistration?.();
  } catch {
    // ignore
  }
}

function clearPushRegistrationState() {
  unregisterPushBeforeLogout().catch(() => {});
}

function schedulePushRegistrationAfterAuth() {
  import("./pushNotifications")
    .then((m) =>
      m.registerPushNotifications?.({ force: false }).then((result) => {
        if (result?.ok) {
          console.info("[push] registered after sign-in");
          return;
        }
        if (result?.reason === "denied") {
          import("../utils/toast")
            .then(({ showToast }) => {
              showToast.info(
                "Allow browser notifications when prompted so you receive booking accepts and OTP alerts."
              );
            })
            .catch(() => {});
        }
      })
    )
    .catch(() => {});
}

function forceLogoutToLogin() {
  clearUserScopedStorage();
  persistCustomerProfile(null);
  clearPushRegistrationState();
  if (typeof window === "undefined") return;
  window.location.replace(loginPathForContext(window.location.pathname));
}

function getErrorMessage(data, res) {
  const mapped = resolveMessageFromApiBody(data, res?.status);
  if (mapped) return mapped;

  const candidate =
    data && typeof data === "object"
      ? typeof data.message === "string" && data.message.trim()
        ? data.message
        : typeof data.error === "string" && data.error.trim()
          ? data.error
          : ""
      : typeof data === "string" && data.trim()
        ? data
        : "";

  const looksInternal =
    /relation\s+".*"\s+does\s+not\s+exist|column\s+".*"|sql|database|syntax error|query failed/i.test(
      candidate
    );
  const keepProviderMessage = res.status === 502 || res.status === 503;

  if (looksInternal || (res.status >= 500 && !keepProviderMessage)) {
    return "Something went wrong on our side. Please try again.";
  }

  if (res.status === 404 && candidate === "Not found") {
    return "API endpoint not found. Stop the old backend process, then run npm start in nemacareBE again.";
  }

  if (data && typeof data === "object") {
    if (typeof data.message === "string" && data.message.trim()) {
      return data.message;
    }
    if (typeof data.error === "string" && data.error.trim()) {
      return data.error;
    }
  }
  if (typeof data === "string" && data.trim()) {
    return data;
  }
  return res.statusText || `Request failed (${res.status})`;
}

function buildMarketplaceCaregiversQuery(params = {}) {
  const q = new URLSearchParams();
  const {
    latitude,
    longitude,
    radiusKm,
    minRating,
    specialisations,
    emergencyPoolOnly,
    serviceDate,
    date,
    serviceTime,
    time,
    careType,
    gender,
    ageGroup,
    minExperience,
    verifiedOnly,
    religion,
    foodPreference,
    sleepArrangement,
    availableNow,
    shiftType,
    salaryHourlyMax,
    salaryMonthlyMax,
    salaryServiceType,
    patientConditions,
    languages,
    skills,
    nurseClinical,
    nurseQualifications,
    nurseSkills,
    physioRequirements,
    physioFrequency,
    physioDuration,
    consultationType,
    packageKey,
    serviceId,
  } = params;

  if (latitude != null && latitude !== "") q.set("lat", String(latitude));
  if (longitude != null && longitude !== "") q.set("lng", String(longitude));
  if (radiusKm != null && radiusKm !== "") q.set("radiusKm", String(radiusKm));
  if (minRating != null && minRating !== "") q.set("minRating", String(minRating));
  if (Array.isArray(specialisations) && specialisations.length) {
    q.set("specialisations", specialisations.join(","));
  }
  if (emergencyPoolOnly === true) q.set("emergencyPoolOnly", "true");
  const resolvedServiceDate = serviceDate || date;
  if (resolvedServiceDate) q.set("serviceDate", String(resolvedServiceDate).slice(0, 10));
  const resolvedServiceTime = serviceTime || time;
  if (resolvedServiceTime) q.set("serviceTime", String(resolvedServiceTime).slice(0, 5));
  if (careType) q.set("careType", String(careType));
  if (gender) q.set("gender", String(gender));
  if (ageGroup) q.set("ageGroup", String(ageGroup));
  if (minExperience != null && minExperience !== "" && Number(minExperience) > 0) {
    q.set("minExperience", String(minExperience));
  }
  if (verifiedOnly === true || verifiedOnly === "true") q.set("verifiedOnly", "true");
  if (religion) q.set("religion", String(religion));
  if (foodPreference) q.set("foodPreference", String(foodPreference));
  if (sleepArrangement) q.set("sleepArrangement", String(sleepArrangement));
  if (availableNow === true || availableNow === "true") q.set("availableNow", "true");
  if (shiftType) q.set("shiftType", String(shiftType));
  if (salaryHourlyMax != null && salaryHourlyMax !== "") q.set("salaryHourlyMax", String(salaryHourlyMax));
  if (salaryMonthlyMax != null && salaryMonthlyMax !== "") q.set("salaryMonthlyMax", String(salaryMonthlyMax));
  if (salaryServiceType) q.set("salaryServiceType", String(salaryServiceType));
  if (patientConditions) q.set("patientConditions", String(patientConditions));
  if (languages) q.set("languages", String(languages));
  if (skills) q.set("skills", String(skills));
  if (nurseClinical) q.set("nurseClinical", String(nurseClinical));
  if (nurseQualifications) q.set("nurseQualifications", String(nurseQualifications));
  if (nurseSkills) q.set("nurseSkills", String(nurseSkills));
  if (physioRequirements) q.set("physioRequirements", String(physioRequirements));
  if (physioFrequency) q.set("physioFrequency", String(physioFrequency));
  if (physioDuration) q.set("physioDuration", String(physioDuration));
  if (consultationType) q.set("consultationType", String(consultationType));
  if (packageKey) q.set("packageKey", String(packageKey));
  if (serviceId) q.set("serviceId", String(serviceId));
  return q;
}

const inflightGetRequests = new Map();

async function request(path, options = {}) {
  const method = String(options.method || "GET").toUpperCase();
  const canDedupe =
    method === "GET" && options.body == null && !options.signal && !(Number(options.timeoutMs) > 0);
  const dedupeKey = canDedupe
    ? `${method}:${path}:${window.localStorage.getItem(TOKEN_STORAGE_KEY) || ""}`
    : "";
  if (dedupeKey) {
    const existing = inflightGetRequests.get(dedupeKey);
    if (existing) return existing;
  }

  const run = performRequest(path, options);
  if (dedupeKey) {
    inflightGetRequests.set(dedupeKey, run);
    run
      .finally(() => {
        if (inflightGetRequests.get(dedupeKey) === run) inflightGetRequests.delete(dedupeKey);
      })
      .catch(() => {});
  }
  return run;
}

function authPathFromRequestPath(path) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return normalizedPath.split("?")[0];
}

async function performRequest(path, options = {}) {
  const url = `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
  const token = readAuthToken();
  const authPath = authPathFromRequestPath(path);
  const skipAuth =
    options.skipAuth === true ||
    (AUTH_EXEMPT_PATHS.some((p) => authPath.startsWith(p)) &&
      !(options.includeAuthIfPresent && token));
  const headers = {
    ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
    ...(token && !skipAuth ? { Authorization: `Bearer ${token}` } : {}),
    "X-Device-Fingerprint": getOrCreateDeviceFingerprint(),
    ...options.headers,
  };

  const { skipAuth: _skipAuth, timeoutMs = 0, ...fetchOptions } = options;

  let res;
  const fetchPromise = fetch(url, { ...fetchOptions, headers });
  try {
    if (timeoutMs > 0) {
      res = await Promise.race([
        fetchPromise,
        new Promise((_, reject) => {
          window.setTimeout(() => {
            reject(
              new Error(
                `Request timed out after ${Math.round(timeoutMs / 1000)}s — is nemacareBE running on ${API_BASE_URL}?`
              )
            );
          }, timeoutMs);
        }),
      ]);
    } else {
      res = await fetchPromise;
    }
  } catch (cause) {
    if (cause?.name === "AbortError") {
      const abortErr = new Error("Request cancelled");
      abortErr.name = "AbortError";
      abortErr.isAbort = true;
      throw abortErr;
    }
    const err = new Error(
      "Unable to reach the server. Check your connection and that the API is running."
    );
    err.isNetworkError = true;
    err.cause = cause;
    throw err;
  }

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!res.ok) {
    const message = getErrorMessage(data, res);
    const normalizedMessage = String(message || "").toLowerCase();
    const looksLikeDeletedOrMissingUser =
      /user.*not found|account.*not found|caregiver.*not found|customer.*not found|profile.*not found|no such user|deleted user|inactive user/.test(
        normalizedMessage
      );
    const authWasSent = Boolean(token && !skipAuth);
    const missingHeaderOnServer =
      res.status === 401 && /authorization header missing/i.test(String(message || ""));
    const shouldForceLogout =
      authWasSent &&
      !missingHeaderOnServer &&
      (res.status === 401 || ((res.status === 403 || res.status === 404) && looksLikeDeletedOrMissingUser));
    if (shouldForceLogout) {
      forceLogoutToLogin();
    }
    const err = new Error(message);
    err.status = res.status;
    err.body = data;
    err.code = data?.code || data?.error?.code || null;
    err.availableAt = data?.availableAt || null;
    err.freemium = data?.freemium || null;
    err.existingBookingId = data?.existingBookingId || null;
    err.existingBookingNumber = data?.existingBookingNumber || null;
    err.actions = data?.actions || null;
    throw err;
  }

  return data;
}

function normalizeApiPath(p) {
  if (!p || typeof p !== "string") return null;
  const t = p.trim();
  if (!t) return null;
  return t.startsWith("/") ? t : `/${t}`;
}

function getStoredCustomerProfile() {
  const raw = window.localStorage.getItem(CUSTOMER_PROFILE_STORAGE_KEY);
  if (!raw) return null;
  try {
    return normalizeCustomerProfileRecord(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * Unwrap API envelopes ({ user }, { profile }, { data: { user } }) and normalize
 * name fields so header/nav never lose the signed-in customer name.
 */
function normalizeCustomerProfileRecord(input) {
  if (!input || typeof input !== "object") return null;

  let record = input;
  const hasOwnName =
    Boolean(String(input.firstName || input.first_name || "").trim()) ||
    Boolean(String(input.lastName || input.last_name || "").trim()) ||
    Boolean(String(input.fullName || input.name || "").trim()) ||
    Boolean(String(input.mobile || input.phone || "").trim());

  if (!hasOwnName) {
    if (input.user && typeof input.user === "object") record = input.user;
    else if (input.profile && typeof input.profile === "object") record = input.profile;
    else if (input.data?.user && typeof input.data.user === "object") record = input.data.user;
    else if (input.data?.profile && typeof input.data.profile === "object") record = input.data.profile;
  }

  let firstName = String(record.firstName || record.first_name || "").trim();
  let lastName = String(record.lastName || record.last_name || "").trim();
  let fullName = String(record.fullName || record.name || "").trim();
  if (!fullName && (firstName || lastName)) {
    fullName = `${firstName} ${lastName}`.trim();
  }
  if (!firstName && !lastName && fullName) {
    const parts = fullName.split(/\s+/).filter(Boolean);
    firstName = parts[0] || "";
    lastName = parts.length > 1 ? parts.slice(1).join(" ") : "";
  }
  // Strip mobile numbers mistakenly stored as names.
  if (/^\d{8,}$/.test(firstName.replace(/\s+/g, ""))) firstName = "";
  if (/^\d{8,}$/.test(lastName.replace(/\s+/g, ""))) lastName = "";
  if (/^\d{8,}$/.test(fullName.replace(/\s+/g, ""))) fullName = "";
  if (!fullName && (firstName || lastName)) {
    fullName = `${firstName} ${lastName}`.trim();
  }

  const username = String(record.username || "").trim();
  const emailFromUsername = username.includes("@") ? username : "";

  return {
    ...record,
    firstName,
    lastName,
    fullName,
    name: fullName || record.name || "",
    mobile: String(record.mobile || record.phone || "").trim(),
    email: String(record.email || record.mail || "").trim() || emailFromUsername,
    city: String(record.city || record.location || "").trim(),
    location: String(record.location || record.city || "").trim(),
    userId: record.userId || record.id || record.user_id || null,
    profileImageUrl:
      record.profileImageUrl ||
      record.profile_image_url ||
      record.avatarUrl ||
      record.photoUrl ||
      "",
  };
}

function customerProfileHasDisplayName(profile) {
  if (!profile || typeof profile !== "object") return false;
  const first = String(profile.firstName || "").trim();
  const last = String(profile.lastName || "").trim();
  const full = String(profile.fullName || profile.name || "").trim();
  const usable = (value) => {
    if (!value) return false;
    return !/^\d{8,}$/.test(value.replace(/\s+/g, ""));
  };
  return Boolean(usable(first) || usable(last) || usable(full));
}

function persistCustomerProfile(profile) {
  if (!profile || typeof profile !== "object") {
    window.localStorage.removeItem(CUSTOMER_PROFILE_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(CUSTOMER_PROFILE_UPDATED_EVENT, { detail: null }));
    return null;
  }

  const normalized = normalizeCustomerProfileRecord(profile);
  if (!normalized) {
    window.localStorage.removeItem(CUSTOMER_PROFILE_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(CUSTOMER_PROFILE_UPDATED_EVENT, { detail: null }));
    return null;
  }

  let previous = null;
  try {
    const raw = window.localStorage.getItem(CUSTOMER_PROFILE_STORAGE_KEY);
    previous = raw ? normalizeCustomerProfileRecord(JSON.parse(raw)) : null;
  } catch {
    previous = null;
  }

  const incomingHasName = customerProfileHasDisplayName(normalized);
  const previousHasName = customerProfileHasDisplayName(previous);

  // Never clobber a good cached name with an empty / mobile-as-name overwrite.
  const merged =
    previous && !incomingHasName && previousHasName
      ? {
          ...previous,
          ...normalized,
          firstName: previous.firstName,
          lastName: previous.lastName,
          fullName: previous.fullName,
          name: previous.name || previous.fullName,
          mobile: normalized.mobile || previous.mobile,
          city: normalized.city || previous.city || "",
          location: normalized.location || previous.location || previous.city || "",
        }
      : {
          ...(previous || {}),
          ...normalized,
          firstName: incomingHasName
            ? normalized.firstName || previous?.firstName || ""
            : previous?.firstName || normalized.firstName || "",
          lastName: incomingHasName
            ? normalized.lastName || previous?.lastName || ""
            : previous?.lastName || normalized.lastName || "",
          fullName: incomingHasName
            ? normalized.fullName ||
              previous?.fullName ||
              `${normalized.firstName || previous?.firstName || ""} ${
                normalized.lastName || previous?.lastName || ""
              }`.trim()
            : previous?.fullName ||
              normalized.fullName ||
              `${previous?.firstName || ""} ${previous?.lastName || ""}`.trim(),
          mobile: normalized.mobile || previous?.mobile || "",
          city: normalized.city || previous?.city || "",
          location: normalized.location || previous?.location || previous?.city || normalized.city || "",
        };

  if (!merged.fullName && (merged.firstName || merged.lastName)) {
    merged.fullName = `${merged.firstName || ""} ${merged.lastName || ""}`.trim();
  }
  merged.name = merged.fullName || merged.name || "";

  window.localStorage.setItem(CUSTOMER_PROFILE_STORAGE_KEY, JSON.stringify(merged));
  window.dispatchEvent(new CustomEvent(CUSTOMER_PROFILE_UPDATED_EVENT, { detail: merged }));
  return merged;
}

function caregiverDisplayFromOnboarding(onboarding) {
  if (!onboarding || typeof onboarding !== "object") return null;
  const account = onboarding.account || {};
  const basic = onboarding.basic || {};
  const firstName = String(
    account.firstName || account.first_name || basic.firstName || basic.first_name || ""
  ).trim();
  const lastName = String(
    account.lastName || account.last_name || basic.lastName || basic.last_name || ""
  ).trim();
  let name = String(
    account.fullName ||
      basic.fullName ||
      onboarding.name ||
      onboarding.fullName ||
      `${firstName} ${lastName}`.trim() ||
      firstName
  ).trim();
  // Legacy phone-OTP signup stored "Caregiver" as a fake name — ignore it.
  if (/^(caregiver|care\s+attendant|nurse|physiotherapist|physio|therapist)$/i.test(name)) {
    name = "";
  }
  if (!name) return null;
  const nameParts = name.split(/\s+/).filter(Boolean);
  const initials =
    nameParts.length >= 2
      ? `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase()
      : name.charAt(0).toUpperCase();
  const profile = onboarding.profile || {};
  return {
    name,
    initials,
    profileImageUrl:
      profile.profileImageFileUrl ||
      profile.profile_image_file_url ||
      basic.profileImageUrl ||
      account.profileImageFileUrl ||
      onboarding.profileImageUrl ||
      "",
  };
}

function readCaregiverSidebarUser() {
  const userId = getCurrentUserIdFromToken();
  if (!userId) return null;
  try {
    const raw = window.localStorage.getItem(CAREGIVER_SIDEBAR_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (Number(parsed.userId) !== Number(userId)) return null;
    const name = String(parsed.name || "").trim();
    if (!name) return null;
    return {
      name,
      initials: String(parsed.initials || "").trim() || name.charAt(0).toUpperCase(),
      profileImageUrl: String(parsed.profileImageUrl || "").trim(),
    };
  } catch {
    return null;
  }
}

function persistCaregiverSidebarUser(display) {
  const userId = getCurrentUserIdFromToken();
  if (!userId || !display?.name) return null;
  const payload = {
    userId,
    name: String(display.name).trim(),
    initials: String(display.initials || "").trim(),
    profileImageUrl: String(display.profileImageUrl || "").trim(),
    updatedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(CAREGIVER_SIDEBAR_USER_KEY, JSON.stringify(payload));
  } catch {
    // no-op
  }
  return payload;
}

function syncCaregiverSidebarUserFromOnboarding(onboarding) {
  const display = caregiverDisplayFromOnboarding(onboarding);
  if (!display) return null;
  const levelLabel = ROLE_SIDEBAR_CONFIG.caregiver?.user?.role || "Caregiver";
  ROLE_SIDEBAR_CONFIG.caregiver.user = {
    initials: display.initials,
    name: display.name,
    role: levelLabel,
  };
  persistCaregiverSidebarUser(display);
  return display;
}

function notifyCaregiverProfileUpdated(detail = null) {
  if (typeof window === "undefined") return;
  syncCaregiverSidebarUserFromOnboarding(detail);
  window.dispatchEvent(new CustomEvent(CAREGIVER_PROFILE_UPDATED_EVENT, { detail }));
}

function readAuthToken() {
  const token = String(window.localStorage.getItem(TOKEN_STORAGE_KEY) || "").trim();
  return token || null;
}

function decodeAuthTokenPayload() {
  const token = readAuthToken();
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
}

function getCurrentUserIdFromToken() {
  const payload = decodeAuthTokenPayload();
  if (!payload) return null;
  const id = Number(payload?.id);
  return Number.isFinite(id) ? id : null;
}

function getAuthRoleFromToken() {
  const payload = decodeAuthTokenPayload();
  if (!payload) return null;
  const role = String(
    payload.role ??
      payload.Role ??
      payload.userType ??
      payload.user_type ??
      payload.userRole ??
      payload.user_role ??
      ""
  )
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (role === "CARE_GIVER") return "CAREGIVER";
  return role || null;
}

function isCustomerAuthRole() {
  return getAuthRoleFromToken() === "CUSTOMER";
}

/** Avoid hitting customer-only routes when signed in as caregiver / admin (prevents 403 overlay noise). */
function requestAsCustomer(path, options) {
  const role = getAuthRoleFromToken();
  if (role && role !== "CUSTOMER") {
    const err = new Error("Customer access required");
    err.status = 403;
    err.code = "ROLE_MISMATCH";
    return Promise.reject(err);
  }
  return request(path, options);
}

function readHourlyWalletBalance() {
  try {
    const raw = window.localStorage.getItem(HOURLY_WALLET_STORAGE_KEY);
    const parsed = Number(raw || 0);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

/** Authoritative wallet balance from the API (browse-state). */
async function fetchHourlyWalletBalance() {
  if (!isCustomerAuthRole()) return readHourlyWalletBalance();
  const res = await requestAsCustomer("/api/marketplace/browse-state", { method: "GET" });
  const balance = Number(res?.state?.walletBalanceInr);
  const normalized = Number.isFinite(balance) ? balance : 0;
  writeHourlyWalletBalance(normalized);
  return normalized;
}

function writeHourlyWalletBalance(value) {
  try {
    window.localStorage.setItem(
      HOURLY_WALLET_STORAGE_KEY,
      String(Math.max(0, Number(value || 0)))
    );
  } catch {
    // no-op
  }
}

async function topUpHourlyWallet(amountInr = 500) {
  const amount = Math.max(0, Number(amountInr) || 0);
  if (!amount) return readHourlyWalletBalance();
  const res = await request("/api/marketplace/wallet/top-up", {
    method: "POST",
    body: JSON.stringify({ amountInr: amount }),
  });
  const balance = Number(res?.state?.walletBalanceInr ?? res?.balance);
  if (!Number.isFinite(balance)) {
    throw new Error("Wallet top-up did not return a balance.");
  }
  writeHourlyWalletBalance(balance);
  return balance;
}

function normalizeCustomerAddOns(value) {
  const parsed = value && typeof value === "object" ? value : {};
  return {
    sisEmergencyLayer: Boolean(parsed.sisEmergencyLayer),
    clockHealthMonitoring: Boolean(parsed.clockHealthMonitoring),
    advancedAnalytics: Boolean(parsed.advancedAnalytics),
  };
}

async function fetchCustomerAddOns() {
  const res = await request("/api/customer/add-ons", { method: "GET" });
  return normalizeCustomerAddOns(res?.addOns);
}

async function saveCustomerAddOnsRemote(addOns) {
  const res = await request("/api/customer/add-ons", {
    method: "PUT",
    body: JSON.stringify(addOns || {}),
  });
  return normalizeCustomerAddOns(res?.addOns || addOns);
}

/** Ops-only overrides (blocked / waitlisted). Subscription access is derived from payments. */
function readCaregiverAccessStatusOverride() {
  try {
    const raw = window.localStorage.getItem(CAREGIVER_ACCESS_STATUS_STORAGE_KEY);
    const value = String(raw || "").trim().toUpperCase();
    if (value === "BLOCKED" || value === "WAITLISTED") return value;
    return null;
  } catch {
    return null;
  }
}

function writeCaregiverAccessStatus(status) {
  const normalized = String(status || "").trim().toUpperCase();
  const next = normalized === "BLOCKED" || normalized === "WAITLISTED" ? normalized : null;
  try {
    if (next) {
      window.localStorage.setItem(CAREGIVER_ACCESS_STATUS_STORAGE_KEY, next);
    } else {
      window.localStorage.removeItem(CAREGIVER_ACCESS_STATUS_STORAGE_KEY);
    }
  } catch {
    // no-op
  }
  return next;
}

function caregiverReactivationPayableTotal() {
  const base = PAYMENT_AMOUNTS.CAREGIVER_REACTIVATION_FEE;
  return Math.round(base * (1 + GST_RATE));
}

function mapServerFeeTransactionToCaregiverPayment(row) {
  if (!row) return null;
  const taxableBase =
    row.taxable_base != null ? Number(row.taxable_base) : PAYMENT_AMOUNTS.CAREGIVER_REACTIVATION_FEE;
  const gstAmount = row.gst_amount != null ? Number(row.gst_amount) : null;
  const totalAmount = Number(row.total_amount ?? taxableBase ?? 0);
  return {
    id: `server_${row.id}`,
    userId: row.user_id,
    type: PAYMENT_TYPES.CAREGIVER_REACTIVATION_FEE,
    amount: totalAmount,
    taxableBase,
    gstAmount,
    paymentStatus: "PAID",
    createdAt: row.created_at,
    source: "server",
  };
}

function mapServerCaregiverReactivationPayments(serverRows) {
  const payments = (serverRows || [])
    .map(mapServerFeeTransactionToCaregiverPayment)
    .filter(Boolean);
  payments.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  return payments;
}

function caregiverReactivationPaymentsPaid(payments) {
  return (
    Array.isArray(payments) &&
    payments.some(
      (e) =>
        String(e.type || "").toUpperCase() === PAYMENT_TYPES.CAREGIVER_REACTIVATION_FEE &&
        String(e.paymentStatus || "").toUpperCase() === "PAID"
    )
  );
}

function resolveCaregiverAccessStatus({ reactivationPaid }) {
  const override = readCaregiverAccessStatusOverride();
  if (override) return override;
  return reactivationPaid ? "ACTIVE" : "PAYMENT_REQUIRED";
}

function getCaregiverAccessStatus() {
  const billing = readCaregiverBillingState();
  return resolveCaregiverAccessStatus({
    reactivationPaid: Boolean(billing.lastPaidAt),
  });
}

async function getOpsPremiumQualificationData() {
  const res = await request("/api/ops/premium-qualification");
  if (!res?.data || !Array.isArray(res.data.rows)) {
    throw new Error("Premium qualification data is unavailable.");
  }
  return { data: res.data };
}

async function updateOpsPremiumQualificationStatus(caregiverId, updates = {}) {
  const res = await request(`/api/ops/premium-qualification/${encodeURIComponent(String(caregiverId))}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
  if (!res?.data || !Array.isArray(res.data.rows)) {
    throw new Error("Premium qualification update did not return data.");
  }
  return { data: res.data };
}

async function fetchCaregiverPremiumCompliance() {
  const res = await request("/api/caregiver/premium-compliance");
  return res?.compliance || null;
}

async function putCaregiverPremiumCompliance(body) {
  const res = await request("/api/caregiver/premium-compliance", {
    method: "PUT",
    body: JSON.stringify(body || {}),
  });
  return res?.compliance || null;
}

async function getOpsInsuranceWorkflowData() {
  const res = await request("/api/ops/insurance-workflow", { method: "GET" });
  return { data: res?.data || null };
}

async function updateOpsInsuranceWorkflowStatus(bookingId, status) {
  await request(`/api/ops/insurance-workflow/${encodeURIComponent(String(bookingId))}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
  return getOpsInsuranceWorkflowData();
}

async function fetchOpsAccessControlAudit(limit = 50) {
  const res = await request(`/api/ops/access-control-audit?limit=${encodeURIComponent(String(limit))}`, {
    method: "GET",
  });
  return Array.isArray(res?.data?.rows) ? res.data.rows : [];
}

function getOrCreateDeviceFingerprint() {
  try {
    const existing = window.localStorage.getItem(DEVICE_FINGERPRINT_STORAGE_KEY);
    if (existing) return existing;
  } catch {
    // no-op
  }
  const fingerprint = [
    navigator?.userAgent || "ua:unknown",
    navigator?.language || "lang:unknown",
    navigator?.platform || "platform:unknown",
    String(new Date().getTimezoneOffset()),
    `${window?.screen?.width || 0}x${window?.screen?.height || 0}`,
    Math.random().toString(36).slice(2, 10),
  ].join("|");
  try {
    window.localStorage.setItem(DEVICE_FINGERPRINT_STORAGE_KEY, fingerprint);
  } catch {
    // no-op
  }
  return fingerprint;
}

function readDeviceLoginAudit() {
  try {
    const raw = window.localStorage.getItem(DEVICE_LOGIN_AUDIT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeDeviceLoginAudit(rows) {
  try {
    window.localStorage.setItem(DEVICE_LOGIN_AUDIT_STORAGE_KEY, JSON.stringify(rows.slice(0, 500)));
  } catch {
    // no-op
  }
}

function getAccountsOnFingerprint(rows, fingerprint) {
  const byUserId = new Map();
  rows
    .filter((r) => r.fingerprint === fingerprint && r.userId)
    .forEach((r) => {
      byUserId.set(String(r.userId), {
        userId: String(r.userId),
        username: String(r.username || `user_${r.userId}`),
        role: String(r.role || ""),
      });
    });
  return Array.from(byUserId.values());
}

function recordDeviceLogin({ userId, username, role } = {}) {
  const fingerprint = getOrCreateDeviceFingerprint();
  const rows = readDeviceLoginAudit();
  rows.unshift({
    id: `dev_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    at: new Date().toISOString(),
    fingerprint,
    userId: userId != null ? String(userId) : "",
    username: String(username || ""),
    role: String(role || ""),
  });
  writeDeviceLoginAudit(rows);
}

function readDpdpConsentLocal() {
  try {
    const raw = window.localStorage.getItem(DPDP_CONSENT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") {
      return {
        consentGiven: false,
        minimizationAccepted: false,
        retentionAccepted: false,
        consentedAt: null,
      };
    }
    return {
      consentGiven: Boolean(parsed.consentGiven),
      minimizationAccepted: Boolean(parsed.minimizationAccepted),
      retentionAccepted: Boolean(parsed.retentionAccepted),
      consentedAt: parsed.consentedAt || null,
    };
  } catch {
    return {
      consentGiven: false,
      minimizationAccepted: false,
      retentionAccepted: false,
      consentedAt: null,
    };
  }
}

function cacheDpdpConsentLocal(next = {}) {
  const payload = {
    consentGiven: Boolean(next.consentGiven),
    minimizationAccepted: Boolean(next.minimizationAccepted),
    retentionAccepted: Boolean(next.retentionAccepted),
    consentedAt: next.consentedAt || new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(DPDP_CONSENT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // no-op
  }
  return payload;
}

async function fetchDpdpConsent() {
  try {
    const res = await request("/api/customer/dpdp-consent", { method: "GET" });
    const consent = res?.consent || res?.data?.consent || null;
    if (consent && typeof consent === "object") {
      return cacheDpdpConsentLocal(consent);
    }
  } catch (err) {
    if (IS_PRODUCTION) throw err;
  }
  return readDpdpConsentLocal();
}

async function saveDpdpConsentToServer(next = {}) {
  const payload = cacheDpdpConsentLocal(next);
  const res = await request("/api/customer/dpdp-consent", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  const saved = res?.consent || res?.data?.consent || payload;
  return cacheDpdpConsentLocal(saved);
}

function readOpsAssessments() {
  try {
    const raw = window.localStorage.getItem(OPS_ASSESSMENTS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveOpsAssessment(row) {
  if (!row || typeof row !== "object") return readOpsAssessments();
  const list = readOpsAssessments();
  list.unshift({
    id: `assess_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    createdAt: new Date().toISOString(),
    ...row,
  });
  try {
    window.localStorage.setItem(OPS_ASSESSMENTS_STORAGE_KEY, JSON.stringify(list.slice(0, 300)));
  } catch {
    // no-op
  }
  return readOpsAssessments();
}

function getReplacementCandidates() {
  return [];
}

function getDefaultCustomerPaymentState() {
  return {
    subscriptionStatus: "EXPIRED",
    subscriptionPrice: PAYMENT_AMOUNTS.SUBSCRIPTION,
    securityDepositRequired: PAYMENT_AMOUNTS.DEPOSIT,
    policeVerificationFee: PAYMENT_AMOUNTS.POLICE_VERIFICATION,
    careManagerFee: PAYMENT_AMOUNTS.CARE_MANAGER_FEE,
    wallet: { depositBalance: 0 },
    feeStatus: {
      commitmentDeductedBookingIds: [],
      activationDeductedBookingIds: [],
    },
    day0ByBooking: {},
  };
}

async function fetchCustomerBillingSnapshot() {
  const userId = getCurrentUserIdFromToken();
  if (!userId || !isCustomerAuthRole()) return null;
  const [subRes, txRes] = await Promise.all([
    requestAsCustomer("/api/billing/customer-subscription-status", { method: "GET" }),
    request("/api/billing/transactions?limit=80", { method: "GET" }),
  ]);
  const subscription = subRes?.subscription ?? subRes;
  const active = Boolean(subscription?.active);
  const month2SubscriptionDue = Boolean(subscription?.month2SubscriptionDue);
  const month1FromDeposit = Boolean(subscription?.month1FromDeposit);
  const defaults = getDefaultCustomerPaymentState();
  const payments = (txRes?.transactions || []).map((row) => {
    const meta = row.metadata && typeof row.metadata === "object" ? row.metadata : {};
    const patientId =
      row.patientId ||
      row.patient_id ||
      meta.patientId ||
      null;
    const patientName =
      row.patientName ||
      row.patient_name ||
      meta.patientName ||
      null;
    return {
      id: row.id,
      type: formatBillingLineCode(row.line_code),
      amount: row.total_amount,
      paymentStatus: "Paid",
      createdAt: row.created_at,
      bookingId: row.booking_id,
      userId: row.user_id,
      patientId: patientId ? String(patientId) : null,
      patientName: patientName ? String(patientName) : null,
    };
  });
  let subscriptionStatus = formatSubscriptionStatus("AWAITING_TRIAL");
  if (active && month1FromDeposit) subscriptionStatus = formatSubscriptionStatus("ACTIVE (Month 1 from deposit)");
  else if (active) subscriptionStatus = formatSubscriptionStatus("ACTIVE");
  else if (month2SubscriptionDue) subscriptionStatus = formatSubscriptionStatus("MONTH 2 DUE");
  return {
    ...defaults,
    subscription,
    subscriptionStatus,
    subscriptionPrice: PAYMENT_AMOUNTS.SUBSCRIPTION,
    platformFeeMonthly: PAYMENT_AMOUNTS.PLATFORM_FEE,
    month2SubscriptionDue,
    month1FromDeposit,
    nextBillingAt: subscription?.activeUntil ?? subscription?.active_until ?? null,
    patients: Array.isArray(subscription?.patients) ? subscription.patients : [],
    wallet: { depositBalance: subscription?.depositBalance ?? subscription?.deposit_balance ?? 0 },
    payments,
    memberId: subscription?.memberId || subscription?.member_id || null,
    subscriptionId: subscription?.subscriptionId || subscription?.subscription_id || null,
  };
}

function defaultCareManagerSubscriptionState() {
  return {
    status: "INACTIVE",
    pricePerMonth: PAYMENT_AMOUNTS.SUBSCRIPTION,
    nextBillingAt: null,
    profileVisibility: "Hidden until payment",
    revenue: 0,
    monthlySubscriptionCost: PAYMENT_AMOUNTS.SUBSCRIPTION,
    note:
      "Pay the monthly listing fee to keep your care manager profile active and visible for new assignments.",
  };
}

function readCareManagerSubscriptionState() {
  try {
    const raw = window.localStorage.getItem(CARE_MANAGER_SUBSCRIPTION_STORAGE_KEY);
    if (!raw) return defaultCareManagerSubscriptionState();
    const parsed = JSON.parse(raw);
    return {
      ...defaultCareManagerSubscriptionState(),
      ...(parsed && typeof parsed === "object" ? parsed : {}),
    };
  } catch {
    return defaultCareManagerSubscriptionState();
  }
}

function writeCareManagerSubscriptionState(next) {
  const merged = {
    ...defaultCareManagerSubscriptionState(),
    ...(next && typeof next === "object" ? next : {}),
  };
  try {
    window.localStorage.setItem(CARE_MANAGER_SUBSCRIPTION_STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // no-op for MVP
  }
  return merged;
}

function defaultCaregiverBillingState() {
  const base = {
    reactivationFee: caregiverReactivationPayableTotal(),
    reactivationFeeBase: PAYMENT_AMOUNTS.CAREGIVER_REACTIVATION_FEE,
    lastPaidAt: null,
    nextReviewAt: null,
    note:
      "Caregiver salary is paid directly by the family. NEMA billing here is limited to account reactivation and insurance tracking.",
  };
  if (IS_PRODUCTION) {
    return {
      ...base,
      insuranceStatus: "—",
      insuranceProvider: "—",
      note: "Billing details load from your account when you sign in.",
    };
  }
  return {
    ...base,
    insuranceStatus: "Queued until Day 30 completion",
    insuranceProvider: "Pending activation",
  };
}

function readCaregiverBillingState() {
  try {
    const raw = window.localStorage.getItem(CAREGIVER_BILLING_STORAGE_KEY);
    if (!raw) return defaultCaregiverBillingState();
    const parsed = JSON.parse(raw);
    return {
      ...defaultCaregiverBillingState(),
      ...(parsed && typeof parsed === "object" ? parsed : {}),
    };
  } catch {
    return defaultCaregiverBillingState();
  }
}

function writeCaregiverBillingState(next) {
  const merged = {
    ...defaultCaregiverBillingState(),
    ...(next && typeof next === "object" ? next : {}),
  };
  try {
    window.localStorage.setItem(CAREGIVER_BILLING_STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // no-op for MVP
  }
  return merged;
}

function readCaregiverCompensationDirectory() {
  try {
    const raw = window.localStorage.getItem(CAREGIVER_COMPENSATION_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeCaregiverCompensationDirectory(directory) {
  try {
    window.localStorage.setItem(
      CAREGIVER_COMPENSATION_STORAGE_KEY,
      JSON.stringify(directory && typeof directory === "object" ? directory : {})
    );
  } catch {
    // no-op
  }
}

function normalizeCaregiverCompensationValue(value) {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? Math.round(amount) : null;
}

function getCaregiverCompensationPreference(caregiverId = getCurrentUserIdFromToken()) {
  if (!caregiverId) return null;
  const directory = readCaregiverCompensationDirectory();
  const saved = directory[String(caregiverId)];
  return saved && typeof saved === "object" ? saved : null;
}

function saveCaregiverCompensationPreference(payload = {}) {
  const caregiverId = Number(payload?.caregiverId || getCurrentUserIdFromToken());
  if (!Number.isFinite(caregiverId) || caregiverId <= 0) return null;
  const directory = readCaregiverCompensationDirectory();
  const next = {
    caregiverId,
    caregiverName: String(payload?.caregiverName || directory[String(caregiverId)]?.caregiverName || "").trim(),
    salaryExpectation: normalizeCaregiverCompensationValue(payload?.salaryExpectation),
    hourlyRateExpectation: normalizeCaregiverCompensationValue(payload?.hourlyRateExpectation),
    updatedAt: new Date().toISOString(),
  };
  directory[String(caregiverId)] = next;
  writeCaregiverCompensationDirectory(directory);
  return next;
}

function buildCaregiverBillingSnapshot({ payments, serverLoaded = false, unavailable = false }) {
  const billing = serverLoaded || !IS_PRODUCTION ? readCaregiverBillingState() : defaultCaregiverBillingState();
  const defaults = defaultCaregiverBillingState();
  const reactivationPaid = caregiverReactivationPaymentsPaid(payments);
  const accessStatus = unavailable ? null : resolveCaregiverAccessStatus({ reactivationPaid });

  const latestPayment = reactivationPaid && payments.length ? payments[0] : null;
  const lastPaidAt = latestPayment?.createdAt || null;
  const nextReviewAt =
    lastPaidAt != null
      ? new Date(new Date(lastPaidAt).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString()
      : null;

  return {
    ...defaults,
    reactivationFee: caregiverReactivationPayableTotal(),
    reactivationFeeBase: defaults.reactivationFeeBase,
    note: billing.note || defaults.note,
    accessStatus,
    payments,
    reactivationPaid,
    serverLoaded,
    lastPaidAt,
    nextReviewAt,
    insuranceStatus: unavailable
      ? "—"
      : reactivationPaid
        ? "Active for current caregiver cycle"
        : defaults.insuranceStatus,
    insuranceProvider: unavailable
      ? "—"
      : reactivationPaid
        ? "NEMA group cover"
        : defaults.insuranceProvider,
    unavailable,
  };
}

function getCaregiverBillingSnapshot() {
  if (IS_PRODUCTION) {
    return buildCaregiverBillingSnapshot({ payments: [], serverLoaded: false, unavailable: true });
  }
  return buildCaregiverBillingSnapshot({ payments: [], serverLoaded: false });
}

async function fetchCaregiverBillingSnapshot() {
  const res = await request("/api/billing/transactions?limit=80", { method: "GET" });
  const serverRows = (res?.transactions || []).filter(
    (row) => String(row.line_code || "").toUpperCase() === "CAREGIVER_REACTIVATION"
  );
  const payments = mapServerCaregiverReactivationPayments(serverRows);
  const snapshot = buildCaregiverBillingSnapshot({ payments, serverLoaded: true });
  if (snapshot.reactivationPaid) {
    writeCaregiverBillingState({
      lastPaidAt: snapshot.lastPaidAt,
      nextReviewAt: snapshot.nextReviewAt,
      insuranceStatus: snapshot.insuranceStatus,
      insuranceProvider: snapshot.insuranceProvider,
    });
  }
  return snapshot;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function shouldRetryCaregiverReactivationPost(err) {
  return Boolean(
    err?.isNetworkError ||
      err?.status === 502 ||
      err?.status === 503 ||
      err?.status === 504
  );
}

async function postCaregiverReactivationFeeToServerWithRetries(maxAttempts = 4) {
  let lastErr;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      await request("/api/billing/fees/reactivation", {
        method: "POST",
        body: JSON.stringify({}),
      });
      return;
    } catch (err) {
      lastErr = err;
      if (!shouldRetryCaregiverReactivationPost(err)) throw err;
      if (attempt < maxAttempts - 1) await delay(400 * (attempt + 1));
    }
  }
  throw lastErr || new Error("Unable to record payment on the server.");
}

/** Records fee on the server (with retries), then refreshes billing from server ledger. */
async function payCaregiverReactivationFee() {
  await postCaregiverReactivationFeeToServerWithRetries();
  const userId = getCurrentUserIdFromToken();
  if (!userId) {
    const e = new Error("Unable to record payment. Please sign in again as a caregiver.");
    e.status = 401;
    throw e;
  }
  const snapshot = await fetchCaregiverBillingSnapshot();
  if (!snapshot?.reactivationPaid) {
    throw new Error("Payment recorded but billing could not be confirmed. Refresh this page.");
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("nema:caregiver-billing-updated"));
  }
  return snapshot;
}

function readAccessControlAudit() {
  return [];
}

function appendAccessControlAudit() {}

function readSecurityFlags() {
  return [];
}

function addSecurityFlag() {}

function dismissSecurityFlag() {}

/** CARE_MANAGER / ADMIN / OPS use the admin-only route; others use the app user route. */
function isOpsPortalUserType(userType) {
  const u = (userType || "").toUpperCase();
  return u === "CARE_MANAGER" || u === "ADMIN" || u === "OPS";
}

function authLoginRequestPath(userType) {
  const single =
    process.env.REACT_APP_LOGIN_USE_SINGLE_ENDPOINT === "1" ||
    process.env.REACT_APP_LOGIN_USE_SINGLE_ENDPOINT === "true";
  if (single) return "/api/auth/login";

  if (isOpsPortalUserType(userType)) {
    return normalizeApiPath(process.env.REACT_APP_ADMIN_LOGIN_PATH) || "/api/auth/login";
  }
  return (
    normalizeApiPath(process.env.REACT_APP_CUSTOMER_LOGIN_PATH) ||
    "/api/auth/user/login"
  );
}

function postAuthLogin(credentials = {}) {
  const { username, password, userType, ...rest } = credentials;
  const normalizedUserType =
    typeof userType === "string" && userType.trim()
      ? userType.trim().toUpperCase()
      : "";
  const path = authLoginRequestPath(normalizedUserType);
  const deviceFingerprint =
    typeof rest.deviceFingerprint === "string" && rest.deviceFingerprint.trim()
      ? rest.deviceFingerprint.trim()
      : getOrCreateDeviceFingerprint();
  const { deviceFingerprint: _ignored, ...restWithoutFp } = rest;
  const payload = normalizedUserType
    ? { username, password, userType: normalizedUserType, deviceFingerprint, ...restWithoutFp }
    : { username, password, deviceFingerprint, ...restWithoutFp };
  return request(path, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export const api = {
  baseUrl: API_BASE_URL,

  get: (path) => request(path, { method: "GET" }),

  post: (path, body) =>
    request(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  put: (path, body) =>
    request(path, {
      method: "PUT",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  delete: (path) => request(path, { method: "DELETE" }),

  /**
   * Sign-in: picks URL from role so customer/caregiver are not sent to the admin-only route.
   * Body: { username, password, userType } (+ optional fields your API expects).
   * Override paths via REACT_APP_CUSTOMER_LOGIN_PATH / REACT_APP_ADMIN_LOGIN_PATH, or set
   * REACT_APP_LOGIN_USE_SINGLE_ENDPOINT=true if the server uses one /api/auth/login for all.
   */
  authLogin: postAuthLogin,

  /** POST — same as authLogin with default userType CUSTOMER. */
  login: (credentials) =>
    postAuthLogin({ ...credentials, userType: credentials?.userType ?? "" }),

  /** POST — same as authLogin with default userType CARE_MANAGER (ops portal). */
  adminLogin: (credentials) =>
    postAuthLogin({ ...credentials, userType: credentials?.userType ?? "" }),


  createUser: (credentials) => request("/api/auth/register", {
    method: "POST",
    body: credentials !== undefined ? JSON.stringify(credentials) : undefined,
  }),

  sendPhoneOtp: (payload) =>
    request("/api/auth/otp/send", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  verifyPhoneOtp: (payload) =>
    request("/api/auth/otp/verify", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  loginWithPhoneOtp: (payload) =>
    request("/api/auth/login/otp", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  requestLoginOtp: (payload) =>
    request("/api/auth/login/otp/request", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  getCustomerKycStatus: () => request("/api/customer/kyc-status", { method: "GET" }),

  submitCustomerKyc: (payload) =>
    request("/api/customer/kyc", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  setToken: (token) => {
    if (token) {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
      try {
        window.sessionStorage.removeItem("nema_push_banner_dismissed");
      } catch {
        /* ignore */
      }
      schedulePushRegistrationAfterAuth();
      return;
    }
    unregisterPushBeforeLogout()
      .finally(() => {
        clearUserScopedStorage();
        persistCustomerProfile(null);
      })
      .catch(() => {
        clearUserScopedStorage();
        persistCustomerProfile(null);
      });
  },

  /** Clear session and return to the public website (landing page). */
  logoutToWebsite: () => {
    unregisterPushBeforeLogout()
      .catch(() => {})
      .finally(() => {
        clearUserScopedStorage();
        persistCustomerProfile(null);
        if (typeof window !== "undefined") {
          window.location.replace(loginPathForContext(window.location.pathname));
        }
      });
  },

  getToken: readAuthToken,

  getAuthRoleFromToken,
  getCurrentUserIdFromToken,

  /** GET /api/users */
  getUsers: () => request("/api/users", { method: "GET" }),

  /** GET /api/users/role-stats — ADMIN only; active + total counts by role */
  getAdminUserRoleStats: () => request("/api/users/role-stats", { method: "GET" }),

  /** GET /api/bookings */
  getBookings: () => request("/api/bookings", { method: "GET" }),

  /** GET /api/billing/fee-schedule — NEMA brief v2.0 fee schedule + GST rules */
  getBillingFeeSchedule: () => request("/api/billing/fee-schedule", { method: "GET" }),

  /** GET /api/billing/transactions */
  getBillingTransactions: (params = {}) => {
    const q = new URLSearchParams();
    if (params.limit != null) q.set("limit", String(params.limit));
    const qs = q.toString();
    return request(`/api/billing/transactions${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  /** GET /api/billing/wallet-credits — purchased passes, minutes, replacements and utilization */
  getWalletCredits: () => request("/api/billing/wallet-credits", { method: "GET" }),

  getCustomerSettings: () => request("/api/customer/settings", { method: "GET" }),

  changeCustomerPassword: (payload) =>
    request("/api/customer/settings/password", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  saveCustomerSettings: (payload) =>
    request("/api/customer/settings", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  listCustomerSupportTickets: () => request("/api/customer/support-tickets", { method: "GET" }),

  createCustomerSupportTicket: (payload) =>
    request("/api/customer/support-tickets", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** GET /api/billing/transactions/:id/invoice — fee invoice owned by the signed-in customer */
  getBillingTransactionInvoice: (transactionId) =>
    request(`/api/billing/transactions/${encodeURIComponent(String(transactionId))}/invoice`, {
      method: "GET",
    }),

  /** POST /api/billing/bookings/:id/post-trial-charges (CUSTOMER) */
  applyPostTrialBillingCharges: (bookingId) =>
    request(`/api/billing/bookings/${bookingId}/post-trial-charges`, { method: "POST" }),

  /** POST /api/billing/fees/reactivation — body: { variant?: 'six_month_membership' } for customers */
  recordBillingReactivation: (payload) =>
    request("/api/billing/fees/reactivation", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  recordBillingCareManagerSubscription: () =>
    request("/api/billing/fees/care-manager-subscription", { method: "POST" }),

  recordBillingCustomerSubscription: (payload) =>
    request("/api/billing/fees/customer-subscription", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** POST /api/billing/fees/care-manager-management — the customer's ₹2,000/month Care Management
   * Fee for ONE patient's subscription (confirmed 2026-10-30: per-patient, not per-account) */
  createCareManagerManagementFeeOrder: (patientId) =>
    request("/api/billing/fees/care-manager-management", {
      method: "POST",
      body: JSON.stringify({ patientId }),
    }),

  /** GET approved Care Managers for the customer-facing marketplace (optional selection) */
  browseCareManagers: (filters) => {
    const params = new URLSearchParams();
    if (filters?.city) params.set("city", filters.city);
    if (filters?.minExperienceYears != null) params.set("minExperienceYears", filters.minExperienceYears);
    const qs = params.toString();
    return request(`/api/customer/care-managers${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  getCareManagerMarketplaceProfile: (careManagerUserId) =>
    request(`/api/customer/care-managers/${careManagerUserId}`, { method: "GET" }),

  /** GET Care Manager subscription status across all of this customer's patients */
  getPatientCareManagerStatusList: () =>
    request("/api/customer/patients/care-manager-status", { method: "GET" }),

  getPatientCareManagerStatus: (patientId) =>
    request(`/api/customer/patients/${patientId}/care-manager`, { method: "GET" }),

  selectPatientCareManager: (patientId, careManagerUserId) =>
    request(`/api/customer/patients/${patientId}/care-manager/select`, {
      method: "POST",
      body: JSON.stringify({ careManagerUserId }),
    }),

  renewPatientCareManager: (patientId) =>
    request(`/api/customer/patients/${patientId}/care-manager/renew`, { method: "POST" }),

  changePatientCareManager: (patientId, careManagerUserId) =>
    request(`/api/customer/patients/${patientId}/care-manager/change`, {
      method: "POST",
      body: JSON.stringify({ careManagerUserId }),
    }),

  recordBillingIntroCall: (payload) =>
    request("/api/billing/fees/intro-call", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  recordBillingPoliceNewEngagement: (payload) =>
    request("/api/billing/fees/police-new-engagement", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** GET /api/caregivers */
  getCaregivers: () => request("/api/caregivers", { method: "GET" }),

  /** GET /caregivers/nearby?latitude=&longitude=&radiusKm=&minRating=&category= */
  updateCaregiverLocation: ({ latitude, longitude, city, location } = {}) =>
    request("/api/caregiver/location", {
      method: "PUT",
      body: JSON.stringify({ latitude, longitude, city, location }),
    }),

  updateCustomerLocation: ({ latitude, longitude, city, location } = {}) =>
    request("/api/customer/location", {
      method: "PUT",
      body: JSON.stringify({ latitude, longitude, city, location }),
    }),

  getNearbyCaregivers: ({ latitude, longitude, radiusKm, minRating, category, emergencyPoolOnly } = {}) => {
    const q = new URLSearchParams();
    if (latitude != null && latitude !== "") q.set("lat", String(latitude));
    if (longitude != null && longitude !== "") q.set("lng", String(longitude));
    if (radiusKm != null && radiusKm !== "") q.set("radiusKm", String(radiusKm));
    if (minRating != null && minRating !== "") q.set("minRating", String(minRating));
    if (category && category !== "all") q.set("category", String(category));
    if (emergencyPoolOnly === true) q.set("emergencyPoolOnly", "true");
    const query = q.toString();
    return request(`/api/caregivers/nearby${query ? `?${query}` : ""}`, { method: "GET" });
  },

  /** GET marketplace discovery (qualified vs hidden by compliance) */
  getMarketplaceCaregivers: (params = {}) => {
    const { signal, ...queryParams } = params;
    const q = buildMarketplaceCaregiversQuery(queryParams);
    if (!q.toString()) {
      return Promise.reject(new Error("latitude and longitude are required"));
    }
    const jwt = readAuthToken();
    if (!jwt) {
      return request(`/api/marketplace/public/caregivers?${q}`, { method: "GET", signal, skipAuth: true });
    }
    return request(`/api/marketplace/caregivers?${q}`, { method: "GET", signal });
  },

  /** Public caregiver browse — no login required */
  getPublicMarketplaceCaregivers: (params = {}) => {
    const { signal, ...queryParams } = params;
    const q = buildMarketplaceCaregiversQuery(queryParams);
    if (!q.toString()) {
      return Promise.reject(new Error("latitude and longitude are required"));
    }
    return request(`/api/marketplace/public/caregivers?${q}`, { method: "GET", signal });
  },

  /** GET caregiver IDs unavailable on a service date (leave/block/not rostered/on duty). */
  getCaregiversUnavailableOnDate: (params = {}) => {
    const q = new URLSearchParams();
    const date = String(params.serviceDate || params.date || "").slice(0, 10);
    if (date) q.set("serviceDate", date);
    const ids = Array.isArray(params.caregiverIds)
      ? params.caregiverIds
      : String(params.caregiverIds || params.ids || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
    if (ids.length) q.set("caregiverIds", ids.join(","));
    if (params.leaveOnly) q.set("leaveOnly", "1");
    const serviceTime = String(params.serviceTime || params.time || "").slice(0, 5);
    if (serviceTime) q.set("serviceTime", serviceTime);
    const query = q.toString();
    // Always use the public route — same data, avoids customer KYC gate blocking the check.
    return request(`/api/marketplace/public/unavailable-on-date${query ? `?${query}` : ""}`, {
      method: "GET",
      skipAuth: true,
    });
  },

  getPublicCaregiverProfile: (caregiverId) =>
    request(`/api/marketplace/public/caregivers/${caregiverId}`, {
      method: "GET",
      includeAuthIfPresent: true,
    }),

  getCaregiverWhatsappContact: (caregiverId, { authenticated = false } = {}) => {
    const useAuth =
      authenticated || Boolean(window.localStorage.getItem(TOKEN_STORAGE_KEY));
    const base = useAuth
      ? `/api/marketplace/caregivers/${caregiverId}/whatsapp`
      : `/api/marketplace/public/caregivers/${caregiverId}/whatsapp`;
    return request(base, { method: "GET" });
  },

  getFreemiumStatus: () => {
    if (!readAuthToken()) {
      return Promise.resolve({ freemium: null });
    }
    if (!isCustomerAuthRole()) {
      return Promise.resolve({ freemium: null });
    }
    return requestAsCustomer("/api/marketplace/freemium-status", { method: "GET" });
  },

  purchaseMarketplaceAccess: () =>
    request("/api/billing/fees/marketplace-access", { method: "POST", body: "{}" }),

  getPublicHowItWorks: (params) => {
    const q = new URLSearchParams();
    const { latitude, longitude, cityLabel } = params || {};
    if (latitude != null && latitude !== "") q.set("lat", String(latitude));
    if (longitude != null && longitude !== "") q.set("lng", String(longitude));
    if (cityLabel) q.set("city", String(cityLabel));
    const query = q.toString();
    return request(`/api/public/landing/how-it-works${query ? `?${query}` : ""}`, {
      method: "GET",
      skipAuth: true,
    });
  },

  /** GET marketplace task catalog */
  getMarketplaceTasks: () => request("/api/marketplace/tasks", { method: "GET" }),

  /** GET marketplace service types */
  getMarketplaceServiceTypes: () => request("/api/marketplace/service-types", { method: "GET" }),

  /** GET whether an active care manager can be auto-assigned on booking */
  getMarketplaceCareManagerAvailability: () =>
    request("/api/marketplace/care-manager-availability", { method: "GET" }),

  /** GET emergency-pool caregivers available near customer (verified + pool on) */
  getMarketplaceEmergencyPool: ({ latitude, longitude, radiusKm } = {}) => {
    const q = new URLSearchParams();
    if (latitude != null) q.set("lat", String(latitude));
    if (longitude != null) q.set("lng", String(longitude));
    if (radiusKm != null) q.set("radiusKm", String(radiusKm));
    const query = q.toString();
    return request(`/api/marketplace/emergency-pool${query ? `?${query}` : ""}`, { method: "GET" });
  },

  /** POST booking estimate for marketplace flow */
  estimateMarketplaceBooking: (payload) =>
    request("/api/marketplace/bookings/estimate", {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST booking confirmation for marketplace flow */
  confirmMarketplaceBooking: (payload) =>
    request("/api/marketplace/bookings/confirm", {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  getMarketplaceBrowseState: () =>
    requestAsCustomer("/api/marketplace/browse-state", { method: "GET" }),

  topUpMarketplaceWallet: (amountInr) =>
    request("/api/marketplace/wallet/top-up", {
      method: "POST",
      body: JSON.stringify({ amountInr }),
    }),

  getIntroCallConfig: () => request("/api/marketplace/intro-calls/config", { method: "GET" }),

  getIntroCallQuota: () => request("/api/marketplace/intro-calls/quota", { method: "GET" }),

  getTalkWalletHistory: () => request("/api/marketplace/talk-wallet/history", { method: "GET" }),

  createTalkWalletRechargeOrder: () =>
    request("/api/marketplace/talk-wallet/recharge/razorpay-order", { method: "POST" }),

  confirmTalkWalletRechargePayment: (payload) =>
    request("/api/marketplace/talk-wallet/recharge/confirm-payment", {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  purchaseAssistedMatchmaking: (payload) =>
    request("/api/marketplace/talk-wallet/assisted-matchmaking", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  startIntroCall: (payload) =>
    request("/api/marketplace/intro-calls/start", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  getIntroCallSession: (sessionId) =>
    request(`/api/marketplace/intro-calls/${sessionId}`, { method: "GET" }),

  endIntroCallSession: (sessionId, payload) =>
    request(`/api/marketplace/intro-calls/${sessionId}/end`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  heartbeatIntroCallSession: (sessionId) =>
    request(`/api/marketplace/intro-calls/${sessionId}/heartbeat`, {
      method: "POST",
      body: "{}",
    }),

  resumeIntroCallSession: (sessionId) =>
    request(`/api/marketplace/intro-calls/${sessionId}/resume`, {
      method: "POST",
      body: "{}",
    }),

  postIntroCallSignal: (sessionId, message) =>
    request(`/api/marketplace/intro-calls/${sessionId}/signal`, {
      method: "POST",
      body: JSON.stringify(message || {}),
    }),

  listIntroCallSignals: (sessionId, since = 0) =>
    request(`/api/marketplace/intro-calls/${sessionId}/signals?since=${since}`, {
      method: "GET",
    }),

  getCaregiverPendingIntroCalls: () =>
    request("/api/caregiver/intro-calls/pending", { method: "GET" }),

  getCaregiverIntroCallSession: (sessionId) =>
    request(`/api/caregiver/intro-calls/${sessionId}`, { method: "GET" }),

  acceptCaregiverIntroCall: (sessionId) =>
    request(`/api/caregiver/intro-calls/${sessionId}/accept`, { method: "POST" }),

  endCaregiverIntroCall: (sessionId, payload) =>
    request(`/api/caregiver/intro-calls/${sessionId}/end`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  postCaregiverIntroCallSignal: (sessionId, message) =>
    request(`/api/caregiver/intro-calls/${sessionId}/signal`, {
      method: "POST",
      body: JSON.stringify(message || {}),
    }),

  listCaregiverIntroCallSignals: (sessionId, since = 0) =>
    request(`/api/caregiver/intro-calls/${sessionId}/signals?since=${since}`, {
      method: "GET",
    }),

  /** GET /api/ops/dashboard */
  getOpsDashboard: () => request("/api/ops/dashboard", { method: "GET" }),

  /** GET /api/ops/global-search — executive dashboard unified search */
  searchAdminGlobal: (params = {}) => {
    const q = new URLSearchParams();
    if (params.q) q.set("q", String(params.q));
    if (params.limit != null) q.set("limit", String(params.limit));
    const qs = q.toString();
    return request(`/api/ops/global-search${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  /** GET /api/ops/verification */
  getOpsVerification: () => request("/api/ops/verification", { method: "GET" }),

  /** GET /api/ops/customer-kyc-queue */
  getCustomerKycQueue: () => request("/api/ops/customer-kyc-queue", { method: "GET" }),

  /** POST /api/ops/customer-kyc-queue/:userId/approve */
  approveCustomerKyc: (userId, payload = {}) =>
    request(`/api/ops/customer-kyc-queue/${encodeURIComponent(userId)}/approve`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** GET /api/ops/intake-assessments */
  getOpsIntakeAssessments: async () => {
    const res = await request("/api/ops/intake-assessments", { method: "GET" });
    return res?.data || { rows: [], caregivers: [], scenarios: [] };
  },

  /** POST /api/ops/intake-assessments */
  createOpsIntakeAssessment: async (payload) => {
    const res = await request("/api/ops/intake-assessments", {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    });
    return res?.item || null;
  },

  /** PATCH /api/ops/intake-assessments/:id */
  updateOpsIntakeAssessment: async (id, payload) => {
    const res = await request(`/api/ops/intake-assessments/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    });
    return res?.item || null;
  },

  /** GET /api/ops/city-liquidity */
  getOpsCityLiquidity: () => request("/api/ops/city-liquidity", { method: "GET" }),

  /** GET /api/ops/revenue-billing */
  getOpsRevenueBilling: () => request("/api/ops/revenue-billing", { method: "GET" }),

  /** Admin SOP modules — live backend data */
  getAdminSopDashboardSnapshot: () => request("/api/ops/sop/dashboard-snapshot", { method: "GET" }),
  getAdminSopCustomers: () => request("/api/ops/sop/customers", { method: "GET" }),
  createAdminCustomer: (payload) =>
    request("/api/ops/sop/customers", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  createAdminCaregiver: (payload) =>
    request("/api/ops/sop/caregivers", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  getAdminSopCareManagers: () => request("/api/ops/sop/care-managers", { method: "GET" }),
  getAdminSopNcidRows: () => request("/api/ops/sop/ncid", { method: "GET" }),
  getAdminSopNpidRows: () => request("/api/ops/sop/ncid", { method: "GET" }),
  patchAdminSopNcid: (caregiverId, payload) =>
    request(`/api/ops/sop/ncid/${encodeURIComponent(String(caregiverId))}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  patchAdminSopNpid: (caregiverId, payload) =>
    request(`/api/ops/sop/ncid/${encodeURIComponent(String(caregiverId))}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  getAdminSopNcidDetail: (caregiverId) =>
    request(`/api/ops/sop/ncid/${encodeURIComponent(String(caregiverId))}/detail`, { method: "GET" }),
  getAdminSopNpidDetail: (caregiverId) =>
    request(`/api/ops/sop/ncid/${encodeURIComponent(String(caregiverId))}/detail`, { method: "GET" }),
  postAdminSopNcidSync: (caregiverId) =>
    request(`/api/ops/sop/ncid/${encodeURIComponent(String(caregiverId))}/sync`, { method: "POST" }),
  postAdminSopNpidSync: (caregiverId) =>
    request(`/api/ops/sop/ncid/${encodeURIComponent(String(caregiverId))}/sync`, { method: "POST" }),
  postAdminSopNcidActivate: (caregiverId) =>
    request(`/api/ops/sop/ncid/${encodeURIComponent(String(caregiverId))}/activate`, { method: "POST" }),
  postAdminSopNpidActivate: (caregiverId) =>
    request(`/api/ops/sop/ncid/${encodeURIComponent(String(caregiverId))}/activate`, { method: "POST" }),

  getNcidCard: (caregiverId) =>
    request(`/api/ops/ncid-cards/${encodeURIComponent(String(caregiverId))}`, { method: "GET" }),

  downloadNcidCardFile: async (cardId, format = "pdf") => {
    const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    const url = `${API_BASE_URL}/api/ops/ncid-cards/file/${encodeURIComponent(String(cardId))}?format=${encodeURIComponent(format)}&_=${Date.now()}`;
    const res = await fetch(url, {
      method: "GET",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || "Unable to download NCID card");
    }
    const blob = await res.blob();
    const disposition = res.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^"]+)"?/i);
    return {
      blob,
      filename: match?.[1] || `ncid-card.${format}`,
      templateVersion: res.headers.get("X-Ncid-Template") || "",
    };
  },

  generateNcidCard: (caregiverId) =>
    request(`/api/ops/ncid-cards/${encodeURIComponent(String(caregiverId))}/generate`, { method: "POST" }),

  generateCustomerNcidCard: (userId) =>
    request(`/api/ops/ncid-cards/customer/${encodeURIComponent(String(userId))}/generate`, { method: "POST" }),

  getCustomerNcidCard: (userId) =>
    request(`/api/ops/ncid-cards/customer/${encodeURIComponent(String(userId))}`, { method: "GET" }),

  downloadNcidCard: (cardId) =>
    request(`/api/ops/ncid-cards/file/${encodeURIComponent(String(cardId))}`, { method: "GET" }),

  shareNcidCard: (cardId, channel) =>
    request(`/api/ops/ncid-cards/file/${encodeURIComponent(String(cardId))}/share`, {
      method: "POST",
      body: JSON.stringify({ channel }),
    }),
  postAdminSopNcidRecompute: (caregiverId) =>
    request(`/api/ops/sop/ncid/${encodeURIComponent(String(caregiverId))}/recompute`, { method: "POST" }),
  postAdminSopNpidRecompute: (caregiverId) =>
    request(`/api/ops/sop/ncid/${encodeURIComponent(String(caregiverId))}/recompute`, { method: "POST" }),
  getPublicNcidVerification: ({ ncid, npid, caregiverId } = {}) => {
    const qs = new URLSearchParams();
    const code = ncid || npid;
    if (code) qs.set("ncid", code);
    if (caregiverId) qs.set("caregiverId", String(caregiverId));
    return request(`/api/marketplace/public/ncid/verify?${qs.toString()}`, { method: "GET", skipAuth: true });
  },
  getPublicNpidVerification: ({ ncid, npid, caregiverId } = {}) => {
    const qs = new URLSearchParams();
    const code = ncid || npid;
    if (code) qs.set("ncid", code);
    if (caregiverId) qs.set("caregiverId", String(caregiverId));
    return request(`/api/marketplace/public/ncid/verify?${qs.toString()}`, { method: "GET", skipAuth: true });
  },
  getAdminSopSubscriptions: () => request("/api/ops/sop/subscriptions", { method: "GET" }),
  createAdminSopSubscription: (payload) =>
    request("/api/ops/sop/subscriptions", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  patchAdminSopSubscription: (customerId, payload) =>
    request(`/api/ops/sop/subscriptions/${encodeURIComponent(String(customerId))}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  patchAdminSopPatientSubscription: (subscriptionId, payload) =>
    request(`/api/ops/sop/patient-subscriptions/${encodeURIComponent(String(subscriptionId))}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  getAdminSopPayments: () => request("/api/ops/sop/payments", { method: "GET" }),
  patchAdminSopPayment: (transactionId, payload) =>
    request(`/api/ops/sop/payments/${encodeURIComponent(String(transactionId))}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  getAdminSopPaymentInvestigation: (transactionId) =>
    request(`/api/ops/sop/payments/${encodeURIComponent(String(transactionId))}/investigation`, {
      method: "GET",
    }),
  exportAdminSopPayments: () => request("/api/ops/sop/payments/export", { method: "GET" }),
  verifyAdminSopCareManager: (userId, payload) =>
    request(`/api/ops/sop/care-managers/${encodeURIComponent(String(userId))}/verify`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  transitionAdminSopCareManagerOnboardingStage: (userId, payload) =>
    request(`/api/ops/sop/care-managers/${encodeURIComponent(String(userId))}/onboarding-stage`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  patchAdminSopCareManagerInterview: (userId, payload) =>
    request(`/api/ops/sop/care-managers/${encodeURIComponent(String(userId))}/interview`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  patchAdminSopCareManagerBgvChecklist: (userId, payload) =>
    request(`/api/ops/sop/care-managers/${encodeURIComponent(String(userId))}/bgv-checklist`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  getAdminCareManagerCaseTransferTargets: (caseId) =>
    request(`/api/ops/sop/care-manager-cases/${encodeURIComponent(String(caseId))}/transfer-targets`, {
      method: "GET",
    }),
  transferAdminCareManagerCase: (caseId, payload) =>
    request(`/api/ops/sop/care-manager-cases/${encodeURIComponent(String(caseId))}/transfer`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  runAdminCareManagerSubscriptionExpiryCycle: () =>
    request("/api/ops/sop/care-managers/subscription-expiry-cycle", { method: "POST" }),
  patchAdminCareManagerProfile: (userId, payload) =>
    request(`/api/ops/sop/care-managers/${encodeURIComponent(String(userId))}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  getAdminSopCareManagerPerformance: (userId) =>
    request(`/api/ops/sop/care-managers/${encodeURIComponent(String(userId))}/performance`, {
      method: "GET",
    }),
  getAdminSopComplaints: () => request("/api/ops/sop/complaints", { method: "GET" }),
  getAdminCaseDirectory: (kind) =>
    request(`/api/ops/sop/case-directory?kind=${encodeURIComponent(kind || "complaints")}`, { method: "GET" }),
  createAdminCaseDirectory: (payload) =>
    request("/api/ops/sop/case-directory", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  patchAdminSopComplaint: (id, payload) =>
    request(`/api/ops/sop/complaints/${encodeURIComponent(String(id))}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  getAdminSopReports: () => request("/api/ops/sop/reports", { method: "GET" }),

  // ─── Ticket Management ──────────────────────────────────────────────────────

  listTickets: ({ status, assigned, type, limit, offset } = {}) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (assigned) params.set("assigned", String(assigned));
    if (type) params.set("type", type);
    if (limit) params.set("limit", String(limit));
    if (offset) params.set("offset", String(offset));
    const qs = params.toString();
    return request(`/api/ops/sop/tickets${qs ? `?${qs}` : ""}`);
  },

  assignTicket: (id, { assignedToUserId }) =>
    request(`/api/ops/sop/tickets/${encodeURIComponent(String(id))}/assign`, {
      method: "POST",
      body: JSON.stringify({ assignedToUserId }),
    }),

  addInvestigationNote: (id, { note }) =>
    request(`/api/ops/sop/tickets/${encodeURIComponent(String(id))}/note`, {
      method: "POST",
      body: JSON.stringify({ note }),
    }),

  resolveTicket: (id, { resolutionNotes }) =>
    request(`/api/ops/sop/tickets/${encodeURIComponent(String(id))}/resolve`, {
      method: "POST",
      body: JSON.stringify({ resolutionNotes }),
    }),

  escalateTicket: (id) =>
    request(`/api/ops/sop/tickets/${encodeURIComponent(String(id))}/escalate`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  getSupportTicketBoard: (params = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && value !== "") search.set(key, String(value));
    });
    const qs = search.toString();
    return request(`/api/ops/sop/support-tickets${qs ? `?${qs}` : ""}`);
  },
  getSupportTicket: (id) =>
    request(`/api/ops/sop/support-tickets/${encodeURIComponent(String(id))}`, { method: "GET" }),
  createSupportTicket: (payload) =>
    request("/api/ops/sop/support-tickets", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateSupportTicket: (id, payload) =>
    request(`/api/ops/sop/support-tickets/${encodeURIComponent(String(id))}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  addSupportTicketMessage: (id, payload) =>
    request(`/api/ops/sop/support-tickets/${encodeURIComponent(String(id))}/message`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  lookupSupportTicketPeople: (q) =>
    request(`/api/ops/sop/support-tickets/lookup?q=${encodeURIComponent(q || "")}`),
  saveSupportTicketSla: (sla) =>
    request("/api/ops/sop/support-tickets/sla", {
      method: "PUT",
      body: JSON.stringify({ sla }),
    }),

  // ─── Reports ────────────────────────────────────────────────────────────────

  getWeeklyReport: ({ startDate, endDate, city, serviceType, channel, view } = {}) => {
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    if (city) params.set("city", city);
    if (serviceType) params.set("serviceType", serviceType);
    if (channel) params.set("channel", channel);
    if (view) params.set("view", view);
    const qs = params.toString();
    return request(`/api/ops/sop/reports/weekly${qs ? `?${qs}` : ""}`);
  },

  getDailyReport: (date) => {
    const params = new URLSearchParams();
    if (date) params.set("date", date);
    const qs = params.toString();
    return request(`/api/ops/sop/reports/daily${qs ? `?${qs}` : ""}`);
  },

  getMonthlyReport: ({ year, month } = {}) => {
    const params = new URLSearchParams();
    if (year) params.set("year", String(year));
    if (month) params.set("month", String(month));
    const qs = params.toString();
    return request(`/api/ops/sop/reports/monthly${qs ? `?${qs}` : ""}`);
  },

  getBookingReports: ({ startDate, endDate, city, serviceType } = {}) => {
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    if (city) params.set("city", city);
    if (serviceType) params.set("serviceType", serviceType);
    const qs = params.toString();
    return request(`/api/ops/sop/reports/bookings${qs ? `?${qs}` : ""}`);
  },
  getAdminSopTrustSafety: () => request("/api/ops/sop/trust-safety", { method: "GET" }),
  getAdminSopCustomerFeedback: (filters = {}) => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") qs.set(key, String(value));
    });
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request(`/api/ops/sop/customer-feedback${suffix}`, { method: "GET" });
  },
  getCaregiverFeedback: () => request("/api/caregiver/feedback", { method: "GET" }),
  getAdminSopCaregivers: () => request("/api/ops/sop/caregivers", { method: "GET" }),
  patchAdminSopCaregiver: (caregiverId, payload) =>
    request(`/api/ops/sop/caregivers/${encodeURIComponent(String(caregiverId))}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  getAdminSopCustomerDetail: (userId) =>
    request(`/api/ops/sop/customers/${encodeURIComponent(String(userId))}`, { method: "GET" }),
  getAdminSopCustomerActivity: (userId) =>
    request(`/api/ops/sop/customers/${encodeURIComponent(String(userId))}/activity`, { method: "GET" }),
  getAdminSopCaregiverActivity: (caregiverId) =>
    request(`/api/ops/sop/caregivers/${encodeURIComponent(String(caregiverId))}/activity`, {
      method: "GET",
    }),
  patchAdminSopCustomerAccount: (userId, payload) =>
    request(`/api/ops/sop/customers/${encodeURIComponent(String(userId))}/account`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  patchAdminSopCustomerProfile: (userId, payload) =>
    request(`/api/ops/sop/customers/${encodeURIComponent(String(userId))}/profile`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  getAdminSopCareManagerDetail: (userId) =>
    request(`/api/ops/sop/care-managers/${encodeURIComponent(String(userId))}`, { method: "GET" }),
  getAdminSopNcidHistory: (caregiverId) =>
    request(`/api/ops/sop/ncid/${encodeURIComponent(String(caregiverId))}/history`, { method: "GET" }),
  getAdminSopNpidHistory: (caregiverId) =>
    request(`/api/ops/sop/ncid/${encodeURIComponent(String(caregiverId))}/history`, { method: "GET" }),
  patchAdminSopTrustSafetyAction: (payload) =>
    request("/api/ops/sop/trust-safety/actions", {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  patchAdminSopTrustSafetyCase: (id, payload) =>
    request(`/api/ops/sop/trust-safety/cases/${encodeURIComponent(String(id))}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  getAdminSopNotifications: () => request("/api/ops/sop/notifications", { method: "GET" }),
  getAdminSopAdminLoginEmails: () => request("/api/ops/sop/admin-login-emails", { method: "GET" }),
  getAdminSopAdminLoginEmailPreview: (roleId) =>
    request(`/api/ops/sop/admin-login-emails/${encodeURIComponent(roleId)}/preview`, { method: "GET" }),
  getAdminSopDailyChecklist: (date) => {
    const params = date ? `?date=${encodeURIComponent(date)}` : "";
    return request(`/api/ops/sop/daily-checklist${params}`, { method: "GET" });
  },
  saveAdminSopDailyChecklistItem: (payload) =>
    request("/api/ops/sop/daily-checklist", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  getAdminLeadCrm: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && String(value).trim() !== "") query.set(key, String(value));
    });
    const qs = query.toString();
    return request(`/api/ops/sop/lead-crm${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  exportAdminLeadCrm: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && String(value).trim() !== "") query.set(key, String(value));
    });
    const qs = query.toString();
    return request(`/api/ops/sop/lead-crm/export${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  getLeadCrmSources: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && String(value).trim() !== "") query.set(key, String(value));
    });
    const qs = query.toString();
    return request(`/api/ops/sop/lead-crm/sources${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  /** GET /api/ops/hourly-pricing — admin hourly booking rates + limits */
  getOpsHourlyPricing: () => request("/api/ops/hourly-pricing", { method: "GET" }),

  /** GET /api/ops/nursing-procedures — Delhi NCR indicative procedure catalogue */
  getOpsNursingProcedures: () => request("/api/ops/nursing-procedures", { method: "GET" }),

  /** PATCH /api/ops/nursing-procedures/:id */
  patchOpsNursingProcedure: (id, payload) =>
    request(`/api/ops/nursing-procedures/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),

  /** GET /api/ops/physio-services — Delhi NCR indicative physiotherapy service catalogue */
  getOpsPhysioServices: () => request("/api/ops/physio-services", { method: "GET" }),

  /** PATCH /api/ops/physio-services/:id */
  patchOpsPhysioService: (id, payload) =>
    request(`/api/ops/physio-services/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),

  /** GET /api/ops/talk-wallet-timing — admin call timing / talk wallet config */
  getOpsTalkWalletTiming: () => request("/api/ops/talk-wallet-timing", { method: "GET" }),

  getOpsTalkWalletAnalytics: ({ days } = {}) => {
    const qs = days ? `?days=${encodeURIComponent(days)}` : "";
    return request(`/api/ops/talk-wallet-analytics${qs}`, { method: "GET" });
  },

  /** PUT /api/ops/talk-wallet-timing */
  updateOpsTalkWalletTiming: (payload) =>
    request("/api/ops/talk-wallet-timing", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  /** POST /api/ops/talk-wallet-timing/restore-defaults */
  restoreOpsTalkWalletTiming: () =>
    request("/api/ops/talk-wallet-timing/restore-defaults", { method: "POST", body: "{}" }),

  /** POST /api/ops/talk-wallet-timing/manual-credit */
  grantOpsTalkWalletCredit: (payload) =>
    request("/api/ops/talk-wallet-timing/manual-credit", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** POST /api/ops/talk-wallet-timing/reset-sandbox */
  resetOpsTalkWalletSandbox: () =>
    request("/api/ops/talk-wallet-timing/reset-sandbox", { method: "POST", body: "{}" }),

  /** PUT /api/ops/hourly-pricing */
  updateOpsHourlyPricing: (payload) =>
    request("/api/ops/hourly-pricing", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  /** POST /api/ops/hourly-pricing/preview */
  previewOpsHourlyPricing: ({ rawTaskMinutes, distanceKmOneWay } = {}) =>
    request("/api/ops/hourly-pricing/preview", {
      method: "POST",
      body: JSON.stringify({
        rawTaskMinutes: Number(rawTaskMinutes),
        distanceKmOneWay: Number(distanceKmOneWay),
      }),
    }),

  /** GET /api/marketplace/hourly-pricing — public active rates (no limits) */
  getMarketplaceHourlyPricing: () => request("/api/marketplace/hourly-pricing", { method: "GET" }),

  getPlatformConfig: () => request("/api/platform/config", { method: "GET", skipAuth: true }),

  getOpsPlatformOverview: () => request("/api/ops/platform/overview", { method: "GET" }),

  getOpsTaskCatalog: () => request("/api/ops/platform/task-catalog", { method: "GET" }),

  updateOpsTaskCatalog: (payload) =>
    request("/api/ops/platform/task-catalog", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  getOpsFeatureFlags: () => request("/api/ops/platform/feature-flags", { method: "GET" }),

  getOpsAdminConsole: () => request("/api/ops/platform/admin-console", { method: "GET" }),

  updateOpsAdminConsole: (payload) =>
    request("/api/ops/platform/admin-console", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  refreshOpsAdminPlatform: () =>
    request("/api/ops/platform/admin-console/refresh", { method: "POST", body: "{}" }),

  updateOpsFeatureFlags: (payload) =>
    request("/api/ops/platform/feature-flags", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  getOpsPlatformContent: () => request("/api/ops/platform/content", { method: "GET" }),

  updateOpsPlatformContent: (payload) =>
    request("/api/ops/platform/content", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  getOpsMarketplaceTypes: () => request("/api/ops/platform/marketplace-types", { method: "GET" }),

  getOpsBookingEngine: () => request("/api/ops/platform/booking-engine", { method: "GET" }),

  updateOpsBookingEngine: (payload) =>
    request("/api/ops/platform/booking-engine", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  updateOpsMarketplaceTypes: (payload) =>
    request("/api/ops/platform/marketplace-types", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  listOpsCaregivers: ({ search } = {}) => {
    const q = new URLSearchParams();
    if (search) q.set("search", String(search));
    const query = q.toString();
    return request(`/api/ops/caregivers${query ? `?${query}` : ""}`, { method: "GET" });
  },

  getOpsCaregiver: (caregiverId) =>
    request(`/api/ops/caregivers/${encodeURIComponent(String(caregiverId))}`, { method: "GET" }),

  getAdminCaregiverAvailabilityMonth: ({ caregiverId, year, month } = {}) => {
    const params = new URLSearchParams();
    if (year != null) params.set("year", String(year));
    if (month != null) params.set("month", String(month));
    const qs = params.toString();
    return request(
      `/api/ops/caregivers/${encodeURIComponent(String(caregiverId))}/availability/month${qs ? `?${qs}` : ""}`,
      { method: "GET" }
    );
  },

  updateOpsCaregiver: (caregiverId, payload) =>
    request(`/api/ops/caregivers/${encodeURIComponent(String(caregiverId))}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),

  getAdminCaregiverCompleteProfile: (caregiverId) =>
    request(`/api/ops/caregivers/${encodeURIComponent(String(caregiverId))}/complete-profile`, { method: "GET" }),

  saveAdminCaregiverCompleteProfile: (caregiverId, payload) =>
    request(`/api/ops/caregivers/${encodeURIComponent(String(caregiverId))}/complete-profile`, {
      method: "PUT",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** GET pending caregiver role/qualification change requests (admin queue). Omit caregiverId for the full queue. */
  listAdminCaregiverProfileChangeRequests: (caregiverId) => {
    const qs = caregiverId ? `?caregiverId=${encodeURIComponent(String(caregiverId))}` : "";
    return request(`/api/ops/caregivers/profile-change-requests${qs}`, { method: "GET" });
  },

  approveAdminCaregiverProfileChangeRequest: (changeRequestId) =>
    request(`/api/ops/caregivers/profile-change-requests/${encodeURIComponent(String(changeRequestId))}/approve`, {
      method: "POST",
    }),

  rejectAdminCaregiverProfileChangeRequest: (changeRequestId, rejectionReason) =>
    request(`/api/ops/caregivers/profile-change-requests/${encodeURIComponent(String(changeRequestId))}/reject`, {
      method: "POST",
      body: JSON.stringify({ rejectionReason: rejectionReason || "" }),
    }),

  getAdminCaregiverWorkingHours: (caregiverId) =>
    request(`/api/ops/caregivers/${encodeURIComponent(String(caregiverId))}/schedule/working-hours`, {
      method: "GET",
    }),

  saveAdminCaregiverWorkingHours: (caregiverId, entries) =>
    request(`/api/ops/caregivers/${encodeURIComponent(String(caregiverId))}/schedule/working-hours`, {
      method: "PUT",
      body: JSON.stringify({ entries: Array.isArray(entries) ? entries : [] }),
    }),

  /** GET /api/ops/security-anomalies */
  getOpsSecurityAnomalies: () => request("/api/ops/security-anomalies", { method: "GET" }),

  /** GET caregiver dashboard summary */
  getCaregiverDashboard: () =>
    request(normalizeApiPath(process.env.REACT_APP_CAREGIVER_DASHBOARD_PATH) || "/api/caregiver/dashboard", {
      method: "GET",
    }),

  /** GET caregiver engagement details (Functionality F — View Engagement) */
  getCaregiverEngagementDetails: (bookingId) =>
    request(`/api/caregiver/engagements/${encodeURIComponent(bookingId)}`, { method: "GET" }),

  /** POST caregiver engagement document metadata (after S3 upload) */
  addCaregiverEngagementDocument: (bookingId, payload) =>
    request(`/api/caregiver/engagements/${encodeURIComponent(bookingId)}/documents`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** GET caregiver booking summary */
  getCaregiverBookingSummary: (bookingId) =>
    request(`/api/caregiver/bookings/${encodeURIComponent(bookingId)}/summary`, { method: "GET" }),

  /** POST caregiver care log submission (role: CAREGIVER only). */
  submitCareLog: (bookingId, payload) =>
    request(`/api/bookings/${encodeURIComponent(bookingId)}/care-logs`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  saveCareLogDraft: (bookingId, payload) =>
    request(`/api/bookings/${encodeURIComponent(bookingId)}/care-logs/draft`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** GET caregiver care logs for a booking (role-scoped). */
  listCareLogs: (bookingId) =>
    request(`/api/bookings/${encodeURIComponent(bookingId)}/care-logs`, { method: "GET" }),

  /** GET caregiver-submitted care notes for the signed-in customer, optionally by patient. */
  listCustomerCareLogs: ({ patientId, limit } = {}) => {
    const q = new URLSearchParams();
    if (patientId) q.set("patientId", String(patientId));
    if (limit) q.set("limit", String(limit));
    const qs = q.toString();
    return request(`/api/customer/care-logs${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  getMyCareManager: () => request("/api/customer/care-manager", { method: "GET" }),

  updateMyCareManagerTask: (taskId, completed) =>
    request(`/api/customer/care-manager/tasks/${encodeURIComponent(taskId)}`, {
      method: "PATCH",
      body: JSON.stringify({ completed: Boolean(completed) }),
    }),

  /**
   * GET caregiver bookings with query params (planned for mockup tabs/search/filters).
   * Backend currently returns full list; BE updates will add server-side filtering.
   */
  getCaregiverBookings: ({ status, search, serviceType, fromDate, toDate, page, limit } = {}) => {
    const q = new URLSearchParams();
    if (status) q.set("status", String(status));
    if (search) q.set("search", String(search));
    if (serviceType) q.set("serviceType", String(serviceType));
    if (fromDate) q.set("fromDate", String(fromDate));
    if (toDate) q.set("toDate", String(toDate));
    if (page != null) q.set("page", String(page));
    if (limit != null) q.set("limit", String(limit));
    const qs = q.toString();
    return request(`/api/bookings${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  /** GET caregiver hourly booking queue (same list as GET /api/bookings for caregiver role). */
  getCaregiverHourlyBookings: () =>
    request(normalizeApiPath(process.env.REACT_APP_CAREGIVER_HOURLY_PATH) || "/api/bookings", {
      method: "GET",
    }),

  /** GET caregiver weekly schedule view (legacy alias; prefer getMyWeeklySchedule). */
  getCaregiverSchedule: ({ fromDate, days } = {}) => {
    const params = new URLSearchParams();
    if (fromDate) params.set("fromDate", fromDate);
    if (days) params.set("days", String(days));
    const qs = params.toString();
    const base =
      normalizeApiPath(process.env.REACT_APP_CAREGIVER_SCHEDULE_PATH) ||
      "/api/caregiver/schedule/weekly";
    return request(`${base}${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  /** GET caregiver profile and level data */
  getCaregiverProfile: () =>
    request(normalizeApiPath(process.env.REACT_APP_CAREGIVER_PROFILE_PATH) || "/api/caregiver/profile", {
      method: "GET",
    }),

  /** GET caregiver earnings */
  getCaregiverEarnings: () =>
    request(normalizeApiPath(process.env.REACT_APP_CAREGIVER_EARNINGS_PATH) || "/api/caregiver/earnings", {
      method: "GET",
    }),

  /** GET caregiver earnings wallet (from fee_transactions) */
  getCaregiverEarningsSummary: () =>
    request("/api/caregiver/earnings-summary", { method: "GET" }),

  /** POST caregiver onboarding register */
  caregiverOnboardingRegister: (payload) =>
    request("/api/caregiver/onboarding/register", {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
      skipAuth: true,
    }),

  /** POST first-party page-view ping (anonymous, unauthenticated) for real visitor counts. */
  recordPageView: (payload) =>
    request("/api/analytics/pageview", {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
      skipAuth: true,
    }).catch(() => null),

  /** GET caregiver onboarding status */
  getCaregiverOnboardingStatus: () =>
    request("/api/caregiver/onboarding/status", { method: "GET" }),

  /** GET complete caregiver profile (multi-section) */
  getCompleteCaregiverProfile: async () => {
    const res = await request("/api/caregiver/complete-profile", { method: "GET" });
    const profile = res?.profile;
    const existingMobile = String(profile?.basic?.mobile || "").replace(/\D/g, "");
    if (profile?.basic && existingMobile.length === 10) {
      profile.basic.mobile = existingMobile.length > 10 ? existingMobile.slice(-10) : existingMobile;
      return res;
    }
    try {
      const status = await request("/api/caregiver/onboarding/status", { method: "GET" });
      const account = status?.onboarding?.account || {};
      const digits = String(account.mobile || account.phone || account.username || "").replace(/\D/g, "");
      const mobile =
        digits.length === 10
          ? digits
          : digits.length >= 12 && digits.startsWith("91")
            ? digits.slice(-10)
            : "";
      if (mobile && profile?.basic) {
        profile.basic.mobile = mobile;
      }
    } catch {
      /* keep profile as returned */
    }
    return res;
  },

  /** PUT complete caregiver profile (multi-section) */
  saveCompleteCaregiverProfile: async (payload) => {
    const res = await request("/api/caregiver/complete-profile", {
      method: "PUT",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    });
    notifyCaregiverProfileUpdated(res?.profile || res);
    return res;
  },

  /** POST a caregiver-initiated role/qualification change request (requires admin approval) */
  requestCaregiverProfileChange: (payload) =>
    request("/api/caregiver/profile-change-requests", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** GET the caregiver's own open (pending) role/qualification change request, if any */
  getOpenCaregiverProfileChangeRequest: () =>
    request("/api/caregiver/profile-change-requests/open", { method: "GET" }),

  /** DELETE (cancel) the caregiver's own pending role/qualification change request */
  cancelCaregiverProfileChangeRequest: (changeRequestId) =>
    request(`/api/caregiver/profile-change-requests/${encodeURIComponent(String(changeRequestId))}`, {
      method: "DELETE",
    }),

  /** PUT caregiver onboarding profile step */
  saveCaregiverOnboardingProfile: async (payload) => {
    const res = await request("/api/caregiver/onboarding/profile", {
      method: "PUT",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    });
    notifyCaregiverProfileUpdated(res?.onboarding || res);
    return res;
  },

  /** PUT caregiver onboarding KYC step */
  saveCaregiverOnboardingKyc: async (payload) => {
    const result = await request("/api/caregiver/onboarding/kyc", {
      method: "PUT",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    });
    return result;
  },

  /** PUT caregiver onboarding police step */
  saveCaregiverOnboardingPolice: async (payload) => {
    const result = await request("/api/caregiver/onboarding/police", {
      method: "PUT",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    });
    return result;
  },

  /** GET customer profile */
  getCustomerProfile: () =>
    requestAsCustomer(
      normalizeApiPath(process.env.REACT_APP_CUSTOMER_PROFILE_PATH) || "/api/customer/profile",
      { method: "GET" }
    ),

  /** GET customer dashboard */
  getCustomerDashboard: () => requestAsCustomer("/api/customer/dashboard", { method: "GET" }),

  /** GET customer bookings (auth) */
  getCustomerBookings: () => requestAsCustomer("/api/customer/bookings", { method: "GET" }),

  getCustomerBooking: (bookingId) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}`, { method: "GET" }),

  /** GET the authoritative invoice lines for one of the customer's bookings */
  getBookingInvoice: (bookingId) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/invoice`, {
      method: "GET",
    }),

  /** POST customer rates caregiver for a booking */
  rateCaregiver: (bookingId, payload) =>
    request(`/api/customer/bookings/${bookingId}/rate`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST caregiver rates customer for a booking */
  rateCustomer: (bookingId, payload) =>
    request(`/api/caregiver/bookings/${bookingId}/rate-customer`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST customer creates emergency hourly booking (nearest emergency-pool caregiver) */
  createCustomerEmergencyBooking: (payload) =>
    request("/api/customer/emergency-booking", {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** PUT caregiver emergency pool on/off (server-side matching) */
  updateCaregiverEmergencyPool: (payload) =>
    request("/api/caregiver/emergency-pool", {
      method: "PUT",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST customer requests emergency for a booking */
  requestEmergency: (bookingId, payload) =>
    request(`/api/customer/bookings/${bookingId}/emergency`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST customer requests caregiver replacement for a booking */
  requestBookingReplacement: (bookingId, payload) =>
    request(`/api/customer/bookings/${bookingId}/request-replacement`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  requestEmergencyReplacement: (bookingId, payload) =>
    request(`/api/customer/bookings/${bookingId}/emergency-replacement`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  getReplacementHistory: () =>
    request("/api/customer/replacement-history", { method: "GET" }),

  /** Police Record Verification (PRV) — optional customer product */
  getBookingPrv: (bookingId) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/prv`, { method: "GET" }),
  createPrvPaymentOrder: (bookingId) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/prv/razorpay-order`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  purchaseBookingPrv: (bookingId, payload) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/prv/purchase`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  skipBookingPrv: (bookingId) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/prv/skip`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  markBookingPrvPaymentPending: (bookingId) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/prv/payment-pending`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  markBookingPrvReportViewed: (bookingId) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/prv/report-viewed`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  getBookingPrvReport: (bookingId) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/prv/report`, { method: "GET" }),
  listOpsPrv: (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", String(params.status));
    if (params.q) q.set("q", String(params.q));
    if (params.executiveUserId) q.set("executiveUserId", String(params.executiveUserId));
    if (params.limit != null) q.set("limit", String(params.limit));
    if (params.offset != null) q.set("offset", String(params.offset));
    const qs = q.toString();
    return request(`/api/ops/prv${qs ? `?${qs}` : ""}`, { method: "GET" });
  },
  patchOpsPrv: (prvId, payload) =>
    request(`/api/ops/prv/${encodeURIComponent(String(prvId))}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  assignOpsPrv: (prvId, payload) =>
    request(`/api/ops/prv/${encodeURIComponent(String(prvId))}/assign`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  uploadOpsPrvDocument: (prvId, formData) =>
    request(`/api/ops/prv/${encodeURIComponent(String(prvId))}/documents`, {
      method: "POST",
      body: formData,
    }),
  downloadOpsPrvDocument: async (prvId, docId) => {
    const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(
      `${API_BASE_URL}/api/ops/prv/${encodeURIComponent(String(prvId))}/documents/${encodeURIComponent(String(docId))}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    if (!res.ok) throw new Error("Unable to download this document.");
    const blob = await res.blob();
    const disposition = res.headers.get("Content-Disposition") || "";
    const match = /filename="([^"]+)"/.exec(disposition);
    return { blob, fileName: match?.[1] || `prv-document-${docId}` };
  },
  approveOpsPrvDocument: (prvId, docId) =>
    request(
      `/api/ops/prv/${encodeURIComponent(String(prvId))}/documents/${encodeURIComponent(String(docId))}/approve`,
      { method: "POST" }
    ),
  checkOpsPrvDuplicate: (prvId) =>
    request(`/api/ops/prv/${encodeURIComponent(String(prvId))}/duplicate-check`, { method: "GET" }),
  reuseBookingPrv: (bookingId) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/prv/reuse`, { method: "POST" }),
  initiateFreshBookingPrv: (bookingId, payload) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/prv/fresh`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  approveBookingQueueException: (bookingId, payload) =>
    request(`/api/ops/bookings/${encodeURIComponent(String(bookingId))}/queue-exception`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  getOpsBookingTimer: (bookingId) =>
    request(`/api/ops/bookings/${encodeURIComponent(String(bookingId))}/timer`, { method: "GET" }),

  /** GET Care Pass replacement quota for the signed-in customer */
  getReplacementQuota: () =>
    request("/api/customer/replacement-quota", { method: "GET" }),

  /** GET open replacement status for a booking */
  getBookingReplacement: (bookingId) =>
    request(`/api/customer/bookings/${bookingId}/replacement`, { method: "GET" }),

  /** POST customer selects a replacement caregiver for an open CRMS request */
  selectBookingReplacement: (bookingId, payload) =>
    request(`/api/customer/bookings/${bookingId}/select-replacement`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** POST customer confirms Ops/Care Manager initiated replacement */
  confirmOpsReplacement: (bookingId, payload) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/confirm-ops-replacement`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  confirmBookingDeployment: (bookingId, payload) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/confirm-deployment`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** POST CareHub: pick another caregiver after deny/timeout (preference-list loop). */
  reselectBookingCaregiver: (bookingId, payload) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/reselect-caregiver`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  retryBookingDutyRequest: (bookingId) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/retry-duty-request`, {
      method: "POST",
    }),

  modifyCustomerBooking: (bookingId, payload) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/modify`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  extendCustomerBooking: (bookingId, payload) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/extend`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  resumeCustomerBookingExtension: (bookingId) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/extend/resume`, {
      method: "POST",
    }),

  rescheduleCustomerBooking: (bookingId, payload) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/change/reschedule`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  replaceCustomerBooking: (bookingId, payload) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/change/replace`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  cancelCustomerBookingChange: (bookingId, payload) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/change/cancel`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  listOpenBookingChangeRequests: () => request("/api/bookings/change-requests/open"),

  respondToBookingChange: (changeRequestId, payload) =>
    request(`/api/bookings/change-requests/${encodeURIComponent(String(changeRequestId))}/respond`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** GET CareHub: up to 3 replacement caregivers after a decline (same date/slot). */
  getBookingReplacementSuggestions: (bookingId, params = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return request(
      `/api/customer/bookings/${encodeURIComponent(String(bookingId))}/replacement-suggestions${qs ? `?${qs}` : ""}`,
      { method: "GET" }
    );
  },

  getAdminCrmsAnalytics: () => request("/api/ops/sop/crms/analytics", { method: "GET" }),
  getAdminCrmsReplacements: (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return request(`/api/ops/sop/crms/replacements${qs ? `?${qs}` : ""}`, { method: "GET" });
  },
  adjustAdminCrmsQuota: (payload) =>
    request("/api/ops/sop/crms/quota-adjust", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  approveAdminEmergencyReplacement: (replacementId) =>
    request(`/api/ops/sop/crms/replacements/${replacementId}/approve-emergency`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  updateAdminCrmsReplacement: (replacementId, payload) =>
    request(`/api/ops/sop/crms/replacements/${replacementId}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
  restoreAdminCrmsCaregiver: (caregiverId) =>
    request(`/api/ops/sop/crms/caregivers/${caregiverId}/restore`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  runAdminCrmsFraudScan: () =>
    request("/api/ops/sop/crms/fraud-scan", { method: "POST", body: JSON.stringify({}) }),

  confirmEmergencyContinuityAssignmentOps: (bookingId, payload) =>
    request(`/api/ops/bookings/${bookingId}/emergency-continuity/confirm-assignment`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  completeEmergencyContinuityFollowUpOps: (bookingId, payload) =>
    request(`/api/ops/bookings/${bookingId}/emergency-continuity/follow-up`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST customer ends engagement */
  endEngagement: (bookingId) =>
    request(`/api/customer/bookings/${bookingId}/end`, { method: "POST" }),

  /** POST mock deposit payment (dev / non-production only) */
  payBookingDepositMock: (bookingId) =>
    request(`/api/bookings/${bookingId}/pay`, { method: "POST" }),

  /** @deprecated Use payBookingDeposit from services/depositPayment.js */
  payBookingDeposit: (bookingId) =>
    request(`/api/bookings/${bookingId}/pay`, { method: "POST" }),

  /** POST Razorpay order for security deposit (production checkout) */
  createBookingDepositOrder: (bookingId) =>
    request(`/api/billing/bookings/${bookingId}/razorpay-order`, { method: "POST" }),

  /** POST engagement-confirmation payment */
  payEngagementConfirmation: (bookingId) =>
    request(`/api/bookings/${bookingId}/pay`, { method: "POST" }),

  /** GET active customer subscription aggregate (per-patient list). */
  getCustomerSubscriptionStatus: () =>
    requestAsCustomer("/api/billing/customer-subscription-status", { method: "GET" }),

  /** GET subscription status for one patient. */
  getPatientSubscriptionStatus: (patientId) =>
    request(`/api/billing/patients/${encodeURIComponent(patientId)}/subscription-status`, {
      method: "GET",
    }),

  /**
   * POST record a customer platform subscription payment for the current
   * 30-day cycle (legacy Month-2 path). Prefer patient-subscription for new flows.
   */
  recordCustomerPlatformSubscription: (payload) =>
    request("/api/billing/fees/customer-subscription", {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST patient-scoped subscription purchase / renew */
  recordPatientSubscription: (payload) =>
    request("/api/billing/fees/patient-subscription", {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** GET engagement-confirmation preview (deposit + police breakdown). */
  getEngagementConfirmationPreview: () =>
    request("/api/billing/engagement-confirmation/preview", { method: "GET" }),

  /** POST mark booking completed (caregiver, IN_PROGRESS → COMPLETED) */
  completeBooking: (bookingId) =>
    request(`/api/bookings/${bookingId}/complete`, { method: "POST" }),

  /** GET care manager cases */
  getCareManagerCases: () => request("/api/care-manager/cases", { method: "GET" }),

  /** GET care manager assignments with summary */
  getCareManagerAssignments: (params = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    const qs = query.toString();
    return request(`/api/care-manager/assignments${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  /** GET care manager assigned bookings (monitor status & payment) */
  getCareManagerBookings: () => request("/api/caremanager/bookings", { method: "GET" }),

  /** GET care manager case logs */
  getCareManagerCaseLogs: (caseId) =>
    request(`/api/care-manager/cases/${caseId}/logs`, { method: "GET" }),

  /** GET care plan builder field options (from platform_settings + DB) */
  getCareManagerCarePlanOptions: () =>
    request("/api/care-manager/care-plan/options", { method: "GET" }),

  /** GET care plan for a case (Care Plan Builder) */
  getCareManagerCarePlan: (caseId) =>
    request(`/api/care-manager/cases/${caseId}/care-plan`, { method: "GET" }),

  /** PUT save care plan for a case */
  saveCareManagerCarePlan: (caseId, payload) =>
    request(`/api/care-manager/cases/${caseId}/care-plan`, {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  /** POST §8 — submit the saved care plan for the customer's review/approval */
  submitCareManagerCarePlan: (caseId) =>
    request(`/api/care-manager/cases/${caseId}/care-plan/submit`, { method: "POST" }),

  /** POST §8 — customer approves a submitted care plan, or requests changes */
  respondToCarePlan: (caseId, payload) =>
    request(`/api/customer/care-manager/cases/${caseId}/care-plan/respond`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** GET remote assessment for a case (Phase 1 Assessments module) */
  getCareManagerAssessment: (caseId) =>
    request(`/api/care-manager/cases/${caseId}/assessment`, { method: "GET" }),

  /** PUT save remote assessment for a case */
  saveCareManagerAssessment: (caseId, payload) =>
    request(`/api/care-manager/cases/${caseId}/assessment`, {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  /** GET all assessments for the care manager (optionally filtered by status) */
  getCareManagerAssessments: (params = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    const qs = query.toString();
    return request(`/api/care-manager/assessments${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  getCareManagerFamilyBoard: (caseId) =>
    request(`/api/care-manager/cases/${caseId}/family-board`, { method: "GET" }),

  assignCareManagerFamilyTask: (caseId, payload) =>
    request(`/api/care-manager/cases/${caseId}/tasks`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  scheduleCareManagerFollowUp: (caseId, payload) =>
    request(`/api/care-manager/cases/${caseId}/follow-ups`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  addCareManagerFamilyUpdate: (caseId, payload) =>
    request(`/api/care-manager/cases/${caseId}/updates`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** GET care-manager qualitative notes for caregiver */
  getCareManagerCaregiverNotes: (caregiverId) =>
    request(`/api/care-manager/caregivers/${caregiverId}/notes`, { method: "GET" }),

  /** POST care-manager qualitative note for caregiver */
  addCareManagerCaregiverNote: (caregiverId, payload) =>
    request(`/api/care-manager/caregivers/${caregiverId}/notes`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** GET care manager replacements */
  getCareManagerReplacements: () =>
    request("/api/care-manager/replacements", { method: "GET" }),

  /** POST care manager select replacement caregiver */
  selectCareManagerReplacementCaregiver: (replacementId, payload) =>
    request(`/api/care-manager/replacements/${replacementId}/select-caregiver`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST care manager request replacement for a case */
  requestCareManagerReplacement: (caseId, payload) =>
    request(`/api/care-manager/cases/${caseId}/request-replacement`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /**
   * Phase 2 — CM-facing Marketplace, AI Matching, Shortlist, Offer/Assignment (§11-14).
   * GET AI-matched caregiver candidates for a case (reuses the marketplace query-param shape).
   */
  searchCareManagerCaregivers: (caseId, params = {}) => {
    const q = buildMarketplaceCaregiversQuery(params);
    const qs = q.toString();
    return request(`/api/care-manager/cases/${caseId}/marketplace-search${qs ? `?${qs}` : ""}`, {
      method: "GET",
    });
  },

  /** GET the current shortlist for a case */
  getCareManagerShortlist: (caseId) =>
    request(`/api/care-manager/cases/${caseId}/shortlist`, { method: "GET" }),

  /** POST add a caregiver to a case's shortlist (re-scores server-side and persists the snapshot) */
  addCareManagerShortlistCandidate: (caseId, payload) =>
    request(`/api/care-manager/cases/${caseId}/shortlist`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** DELETE remove a shortlist entry for a case */
  removeCareManagerShortlistCandidate: (caseId, shortlistId) =>
    request(`/api/care-manager/cases/${caseId}/shortlist/${shortlistId}`, { method: "DELETE" }),

  /** PUT reorder a case's shortlist (array of shortlist ids in the new order) */
  reorderCareManagerShortlist: (caseId, orderedShortlistIds) =>
    request(`/api/care-manager/cases/${caseId}/shortlist/reorder`, {
      method: "PUT",
      body: JSON.stringify({ orderedShortlistIds }),
    }),

  /** POST send an assignment offer for a shortlisted caregiver (starts PENDING_CUSTOMER_APPROVAL) */
  sendCareManagerCaregiverOffer: (caseId, shortlistId) =>
    request(`/api/care-manager/cases/${caseId}/assignment-offers`, {
      method: "POST",
      body: JSON.stringify({ shortlistId }),
    }),

  /** GET the cross-case offers queue for the Offers tab */
  getCareManagerAssignmentOffers: () =>
    request("/api/care-manager/assignment-offers", { method: "GET" }),

  /** GET the current open offer (if any) + flow steps for one case */
  getCareManagerAssignmentStatus: (caseId) =>
    request(`/api/care-manager/cases/${caseId}/assignment-status`, { method: "GET" }),

  /** GET the customer's pending/recent care-manager-proposed caregiver offers */
  getCareAssignmentOffers: () => request("/api/customer/care-assignment-offers", { method: "GET" }),

  /** POST customer approves a proposed caregiver assignment offer (hard gate before the caregiver is told) */
  approveCareAssignmentOffer: (offerId) =>
    request(`/api/customer/care-assignment-offers/${offerId}/approve`, { method: "POST" }),

  /** POST customer rejects a proposed caregiver outright (caregiver is never notified) */
  rejectCareAssignmentOffer: (offerId, reason) =>
    request(`/api/customer/care-assignment-offers/${offerId}/reject`, {
      method: "POST",
      body: JSON.stringify(reason ? { reason } : {}),
    }),

  /** GET the caregiver's pending/recent care-manager assignment offers */
  getCaregiverAssignmentOffers: () => request("/api/caregiver/assignment-offers", { method: "GET" }),

  /** POST caregiver accepts/declines an assignment offer */
  respondToCareAssignmentOffer: (offerId, payload) =>
    request(`/api/caregiver/assignment-offers/${offerId}/respond`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** POST snooze customer payment/access block for 24 hours */
  snoozeCareManagerCustomerAccess: (payload) =>
    request("/api/care-manager/snooze-customer-access", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** GET care manager exception queue (escalations, complaints, disputes) */
  getCareManagerExceptions: (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.type) q.set("type", params.type);
    const query = q.toString();
    return request(`/api/care-manager/exceptions${query ? `?${query}` : ""}`, { method: "GET" });
  },

  createCareManagerException: (payload) =>
    request("/api/care-manager/exceptions", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  updateCareManagerException: (id, payload) =>
    request(`/api/care-manager/exceptions/${encodeURIComponent(String(id))}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),

  getOpsExceptions: (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.type) q.set("type", params.type);
    const query = q.toString();
    return request(`/api/ops/exceptions${query ? `?${query}` : ""}`, { method: "GET" });
  },

  updateOpsException: (id, payload) =>
    request(`/api/ops/exceptions/${encodeURIComponent(String(id))}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),

  /** POST snooze care manager (non-compete) */
  snoozeCareManager: (payload) =>
    request("/api/care-manager/snooze", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** DELETE unsnoooze care manager */
  unsnoozeCareManager: () =>
    request("/api/care-manager/snooze", { method: "DELETE" }),

  /** GET care manager snooze status */
  getCareManagerSnoozeStatus: () =>
    request("/api/care-manager/snooze/status", { method: "GET" }),

  /** GET care manager activity log */
  getCareManagerActivityLog: (params = {}) => {
    const q = new URLSearchParams();
    if (params.limit != null) q.set("limit", String(params.limit));
    if (params.offset != null) q.set("offset", String(params.offset));
    const query = q.toString();
    return request(`/api/care-manager/activity-log${query ? `?${query}` : ""}`, { method: "GET" });
  },

  /** GET care manager revenue summary */
  getCareManagerRevenueSummary: () =>
    request("/api/care-manager/revenue-summary", { method: "GET" }),

  /** GET care manager complaints */
  getCareManagerComplaints: () =>
    request("/api/care-manager/complaints", { method: "GET" }),

  /** GET care manager subscription */
  getCareManagerSubscription: () =>
    request("/api/care-manager/subscription", { method: "GET" }),

  /**
   * PUT care manager auto-renew preference. Phase 0 note: no backend route exists yet for this —
   * the call wrapper is wired up ahead of time so CareManagerSubscription.js can call it and
   * degrade gracefully (hide the toggle) on a 404 until a later phase adds the endpoint.
   */
  updateCareManagerAutoRenew: (payload) =>
    request("/api/care-manager/subscription/auto-renew", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  /**
   * GET a downloadable invoice link for one subscription payment. Phase 0 note: no backend route
   * exists yet for this — see updateCareManagerAutoRenew above for the same caveat.
   */
  getCareManagerInvoice: (paymentId) =>
    request(`/api/care-manager/subscription/invoices/${paymentId}`, { method: "GET" }),

  /** GET care manager qualification & experience profile */
  getCareManagerProfile: () => request("/api/care-manager/profile", { method: "GET" }),

  /** PUT care manager qualification & experience profile */
  saveCareManagerProfile: (payload) =>
    request("/api/care-manager/profile", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  /** POST care manager onboarding register (phone-OTP gated account creation) */
  registerCareManagerOnboarding: (payload) =>
    request("/api/care-manager/onboarding/register", {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
      skipAuth: true,
    }),

  /** GET care manager onboarding status (airlock stages) */
  getCareManagerOnboardingStatus: () =>
    request("/api/care-manager/onboarding/status", { method: "GET" }),

  getCareManagerDispatcherDashboard: () =>
    request("/api/care-manager/dashboard", { method: "GET" }),

  /** PUT care manager rich profile (education, employment, certifications, languages, availability, references) */
  saveCareManagerRichProfile: (payload) =>
    request("/api/care-manager/profile/rich", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  /** POST care manager verification document (one of the 8 fixed doc types) */
  saveCareManagerDocument: (payload) =>
    request("/api/care-manager/profile/documents", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** PUT care manager payroll bank details */
  saveCareManagerBankDetails: (payload) =>
    request("/api/care-manager/profile/bank-details", {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  /** POST care manager terms-of-service acceptance */
  acceptCareManagerTerms: (payload) =>
    request("/api/care-manager/terms/accept", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** GET ops care manager document review queue */
  getOpsCareManagerDocuments: (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.ready) q.set("ready", "1");
    const query = q.toString();
    return request(`/api/ops/care-managers/documents${query ? `?${query}` : ""}`, { method: "GET" });
  },

  /** PATCH ops review care manager documents */
  reviewOpsCareManagerDocuments: (userId, payload) =>
    request(`/api/ops/care-managers/${encodeURIComponent(String(userId))}/documents`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),

  /** GET elder profile */
  getCustomerElderProfile: () => request("/api/customer/elder-profile", { method: "GET" }),

  /** PUT elder profile (primary patient / legacy shape; pass patientId to target a patient) */
  updateCustomerElderProfile: (payload) =>
    request("/api/customer/elder-profile", {
      method: "PUT",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** GET all patients linked to the customer Member ID */
  listCustomerPatients: () => request("/api/customer/patients", { method: "GET" }),

  /** POST create a new patient under the customer Member ID */
  createCustomerPatient: (payload) =>
    request("/api/customer/patients", {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** GET one patient by Patient ID */
  getCustomerPatient: (patientId) =>
    request(`/api/customer/patients/${encodeURIComponent(patientId)}`, { method: "GET" }),

  /** PUT update one patient by Patient ID */
  updateCustomerPatient: (patientId, payload) =>
    request(`/api/customer/patients/${encodeURIComponent(patientId)}`, {
      method: "PUT",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST Care Pass purchase — issues Subscription ID once after success */
  recordCarePassPurchase: (payload) =>
    request("/api/billing/fees/care-pass", {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST confirm Razorpay Care Pass checkout (idempotent by payment id) */
  confirmCarePassCheckoutPayment: (payload) =>
    request("/api/billing/care-pass/confirm-payment", {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  quoteCarePassUpgrade: (patientId, subscriptionId) => {
    const qs = new URLSearchParams({ patientId: String(patientId || "") });
    if (subscriptionId) qs.set("subscriptionId", String(subscriptionId));
    return request(`/api/billing/care-pass/upgrade?${qs.toString()}`);
  },

  applyCarePassUpgrade: (payload) =>
    request("/api/billing/care-pass/upgrade", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  createCarePassUpgradeOrder: (payload) =>
    request("/api/billing/care-pass/upgrade/razorpay-order", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  confirmCarePassUpgradePayment: (payload) =>
    request("/api/billing/care-pass/upgrade/confirm-payment", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  skipCaregiverFeedback: (bookingId) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/feedback/skip`, {
      method: "POST",
    }),

  escalateCaregiverFeedback: (bookingId, payload) =>
    request(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/feedback/escalate`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** GET public Care Pass plan pricing (authoritative from backend fee schedule) */
  getCarePassPlans: (params = {}) => {
    const q = new URLSearchParams();
    if (params.requiredBookings != null) q.set("requiredBookings", String(params.requiredBookings));
    if (params.bookingDays != null) q.set("bookingDays", String(params.bookingDays));
    const qs = q.toString();
    return request(`/api/billing/care-pass/plans${qs ? `?${qs}` : ""}`, {
      method: "GET",
      skipAuth: true,
    });
  },

  /** POST Razorpay order (or sandbox capture) for Care Pass checkout */
  createCarePassPaymentOrder: (payload) =>
    request("/api/billing/care-pass/razorpay-order", {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** GET admin/ops search of customer call-credit wallets */
  listOpsCallCreditWallets: ({ search, page, pageSize } = {}) => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (page) q.set("page", page);
    if (pageSize) q.set("pageSize", pageSize);
    const qs = q.toString();
    return request(`/api/billing/admin/call-credits${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  /** GET admin/ops call-credit ledger for a specific customer */
  getOpsCallCreditLedger: (userId, { page, pageSize } = {}) => {
    const q = new URLSearchParams();
    if (page) q.set("page", page);
    if (pageSize) q.set("pageSize", pageSize);
    const qs = q.toString();
    return request(`/api/billing/admin/call-credits/${encodeURIComponent(userId)}/ledger${qs ? `?${qs}` : ""}`, {
      method: "GET",
    });
  },

  /** GET admin/ops recharge history */
  listOpsCallRecharges: ({ page, pageSize } = {}) => {
    const q = new URLSearchParams();
    if (page) q.set("page", page);
    if (pageSize) q.set("pageSize", pageSize);
    const qs = q.toString();
    return request(`/api/billing/admin/call-credits/recharges${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  /** GET admin/ops call history (Call Logs tab) */
  listOpsCallLogs: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") q.set(key, value);
    });
    const qs = q.toString();
    return request(`/api/messenger/admin/calls${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  /** GET admin/ops call analytics (KPIs, trend, distribution, top customers) */
  getOpsCallAnalytics: ({ dateFrom, dateTo } = {}) => {
    const q = new URLSearchParams();
    if (dateFrom) q.set("dateFrom", dateFrom);
    if (dateTo) q.set("dateTo", dateTo);
    const qs = q.toString();
    return request(`/api/messenger/admin/calls/analytics${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  /**
   * GET a call recording as an authenticated blob (Exotel recording URLs need
   * server-side Basic Auth, so this can't be a plain <audio src>).
   */
  getOpsCallRecording: async (sessionId) => {
    const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(
      `${API_BASE_URL}/api/messenger/admin/calls/${encodeURIComponent(String(sessionId))}/recording`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    if (!res.ok) {
      let message = "Recording not available for this call.";
      try {
        const text = await res.text();
        const body = JSON.parse(text);
        if (body?.message) message = body.message;
        else if (body?.data?.message) message = body.data.message;
      } catch {
        /* use default message */
      }
      throw new Error(message);
    }
    const headerType = (res.headers.get("Content-Type") || "audio/mpeg").split(";")[0].trim();
    const raw = await res.blob();
    if (/json|html|xml|text/.test(headerType) || /json|html|text/.test(raw.type)) {
      throw new Error("Recording not available for this call.");
    }
    const type = headerType.startsWith("audio/") ? headerType : "audio/mpeg";
    const blob = new Blob([await raw.arrayBuffer()], { type });
    return { blob, contentType: type };
  },

  /** GET Care Pass checkout quote for plan + pack */
  getCarePassQuote: ({ planId, packId } = {}) => {
    const q = new URLSearchParams();
    if (planId) q.set("plan", planId);
    if (packId) q.set("pack", packId);
    const qs = q.toString();
    return request(`/api/billing/care-pass/quote${qs ? `?${qs}` : ""}`, { method: "GET", skipAuth: true });
  },

  /** PUT customer profile */
  updateCustomerProfile: (payload) =>
    request(
      normalizeApiPath(process.env.REACT_APP_CUSTOMER_PROFILE_PATH) || "/api/customer/profile",
      {
        method: "PUT",
        body: payload !== undefined ? JSON.stringify(payload) : undefined,
      }
    ),

  getStoredCustomerProfile,
  setStoredCustomerProfile: persistCustomerProfile,
  customerProfileStorageKey: CUSTOMER_PROFILE_STORAGE_KEY,
  customerProfileUpdatedEvent: CUSTOMER_PROFILE_UPDATED_EVENT,
  customerSubscriptionUpdatedEvent: CUSTOMER_SUBSCRIPTION_UPDATED_EVENT,
  hourlyWalletStorageKey: HOURLY_WALLET_STORAGE_KEY,
  readHourlyWalletBalance,
  fetchHourlyWalletBalance,
  writeHourlyWalletBalance,
  topUpHourlyWallet,
  caregiverProfileUpdatedEvent: CAREGIVER_PROFILE_UPDATED_EVENT,
  caregiverDisplayFromOnboarding,
  readCaregiverSidebarUser,
  persistCaregiverSidebarUser,
  notifyCaregiverProfileUpdated,
  fetchCustomerAddOns,
  saveCustomerAddOns: saveCustomerAddOnsRemote,
  getCaregiverAccessStatus,
  saveCaregiverAccessStatus: writeCaregiverAccessStatus,
  fetchCustomerBillingSnapshot,
  readCareManagerSubscriptionState,
  writeCareManagerSubscriptionState,
  getCaregiverCompensationDirectory: readCaregiverCompensationDirectory,
  getCaregiverCompensationPreference,
  saveCaregiverCompensationPreference,
  getCaregiverBillingSnapshot,
  fetchCaregiverBillingSnapshot,
  payCaregiverReactivationFee,
  getCaregiverHubSubscription: () => request("/api/caregiver/subscription", { method: "GET" }),
  purchaseCaregiverHubPlan: (planCode) =>
    request("/api/caregiver/subscription/purchase", {
      method: "POST",
      body: JSON.stringify({ planCode }),
    }),
  purchaseCaregiverHubCredits: (quantity) =>
    request("/api/caregiver/subscription/credits", {
      method: "POST",
      body: JSON.stringify({ quantity }),
    }),
  confirmCaregiverHubPayment: (payload) =>
    request("/api/caregiver/subscription/confirm-payment", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  setCaregiverHubAutoRenew: (enabled) =>
    request("/api/caregiver/subscription/auto-renew", {
      method: "POST",
      body: JSON.stringify({ enabled: Boolean(enabled) }),
    }),
  pauseCaregiverHubSubscription: () =>
    request("/api/caregiver/subscription/pause", {
      method: "POST",
      body: JSON.stringify({}),
    }),
  cancelCaregiverHubSubscription: () =>
    request("/api/caregiver/subscription/cancel", {
      method: "POST",
      body: JSON.stringify({}),
    }),
  downloadCaregiverHubInvoice: (transactionId) =>
    request(`/api/caregiver/subscription/transactions/${encodeURIComponent(String(transactionId))}/invoice`, {
      method: "GET",
    }),
  adjustOpsCaregiverHubCredits: (payload) =>
    request("/api/ops/caregiver-subscriptions/credits", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  restoreOpsCaregiverHubCredit: (payload) =>
    request("/api/ops/caregiver-subscriptions/restore-credit", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  getOpsCaregiverHubReport: () => request("/api/ops/caregiver-subscriptions/report", { method: "GET" }),
  fetchTherapistSubscription: () =>
    request("/api/billing/therapist-subscription", { method: "GET" }),
  activateTherapistSubscription: (payload) =>
    request("/api/billing/fees/therapist-subscription", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  changeTherapistPlan: (payload) =>
    request("/api/billing/fees/therapist-plan-change", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  purchaseTherapistCreditPack: (payload) =>
    request("/api/billing/fees/therapist-credit-pack", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  /** Aliases used by Physio CareHub wallet UI */
  getTherapistSubscription: () =>
    request("/api/billing/therapist-subscription", { method: "GET" }),
  purchaseTherapistCredits: (payload) =>
    request("/api/billing/fees/therapist-credit-pack", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  listMyFeeTransactions: (params = {}) => {
    const q = new URLSearchParams();
    if (params.limit != null) q.set("limit", String(params.limit));
    else q.set("limit", "80");
    return request(`/api/billing/transactions?${q.toString()}`, { method: "GET" });
  },
  adjustOpsBookingCredits: (payload) =>
    request("/api/ops/booking-credits/adjust", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  forceCancelOpsBooking: (bookingId, payload) =>
    request(`/api/ops/bookings/${bookingId}/force-cancel`, {
      method: "POST",
      body: payload || {},
    }),
  forceReassignOpsBooking: (bookingId, payload) =>
    request(`/api/ops/bookings/${bookingId}/force-reassign`, {
      method: "POST",
      body: payload || {},
    }),

  getAdminBookings: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && value !== "") q.set(key, String(value));
    });
    const qs = q.toString();
    return request(`/api/ops/bookings${qs ? `?${qs}` : ""}`, { method: "GET" });
  },
  getAdminBookingsCsv: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && value !== "") q.set(key, String(value));
    });
    q.set("format", "csv");
    return request(`/api/ops/bookings?${q.toString()}`, { method: "GET" });
  },
  getAdminBookingSummary: () => request("/api/ops/bookings/summary", { method: "GET" }),
  getAdminBookingDetail: (bookingId) =>
    request(`/api/ops/bookings/${bookingId}`, { method: "GET" }),
  getAdminBookingHistory: (bookingId) =>
    request(`/api/ops/bookings/${bookingId}/history`, { method: "GET" }),
  getAdminBookingEligibleCaregivers: (bookingId) =>
    request(`/api/ops/bookings/${bookingId}/eligible-caregivers`, { method: "GET" }),
  getAdminRescheduleRequests: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && value !== "") q.set(key, String(value));
    });
    const qs = q.toString();
    return request(`/api/ops/reschedule-requests${qs ? `?${qs}` : ""}`, { method: "GET" });
  },
  approveAdminReschedule: (requestId, payload) =>
    request(`/api/ops/reschedule-requests/${requestId}/approve`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  rejectAdminReschedule: (requestId, payload) =>
    request(`/api/ops/reschedule-requests/${requestId}/reject`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  assignAdminReschedule: (requestId, payload) =>
    request(`/api/ops/reschedule-requests/${requestId}/assign`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  overrideAdminReschedule: (requestId, payload) =>
    request(`/api/ops/reschedule-requests/${requestId}/override-schedule`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  escalateAdminReschedule: (requestId, payload) =>
    request(`/api/ops/reschedule-requests/${requestId}/escalate`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  approveAdminRescheduleQuota: (requestId, payload) =>
    request(`/api/ops/reschedule-quota-requests/${requestId}/approve`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  rejectAdminRescheduleQuota: (requestId, payload) =>
    request(`/api/ops/reschedule-quota-requests/${requestId}/reject`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  reassignAdminBooking: (bookingId, payload) =>
    request(`/api/ops/bookings/${bookingId}/reassign`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  cancelAdminBooking: (bookingId, payload) =>
    request(`/api/ops/bookings/${bookingId}/cancel`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  endShiftAdminBooking: (bookingId, payload) =>
    request(`/api/ops/bookings/${bookingId}/end-shift`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  createAdminShift: (payload) =>
    request("/api/ops/shifts", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  createAdminBooking: (payload) =>
    request("/api/ops/bookings", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  updateAdminShift: (bookingId, payload) =>
    request(`/api/ops/shifts/${bookingId}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),

  getAdminMatchRequests: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && value !== "") q.set(key, String(value));
    });
    const qs = q.toString();
    return request(`/api/ops/matchmaking${qs ? `?${qs}` : ""}`, { method: "GET" });
  },
  getAdminMatchmakingSummary: () => request("/api/ops/matchmaking/summary", { method: "GET" }),
  getAdminMatchRequestDetail: (bookingId) =>
    request(`/api/ops/matchmaking/${bookingId}`, { method: "GET" }),
  getAdminIntegrations: () => request("/api/ops/integrations", { method: "GET" }),
  getAdminAuditCenter: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && value !== "") q.set(key, String(value));
    });
    const qs = q.toString();
    return request(`/api/ops/audit-center${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  getCareHubOverview: () => request("/api/ops/carehub/overview", { method: "GET" }),
  listCareHubDossiers: () => request("/api/ops/carehub/dossiers", { method: "GET" }),
  approveCareHubDossier: (caregiverId) =>
    request(`/api/ops/carehub/dossiers/${encodeURIComponent(caregiverId)}/approve`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  resubmitCareHubDossier: (caregiverId, payload) =>
    request(`/api/ops/carehub/dossiers/${encodeURIComponent(caregiverId)}/resubmit`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  listCareHubTherapists: () => request("/api/ops/carehub/therapists", { method: "GET" }),
  listCareHubBookings: (params = {}) => {
    const q = new URLSearchParams();
    if (params.limit != null) q.set("limit", String(params.limit));
    const qs = q.toString();
    return request(`/api/ops/carehub/bookings${qs ? `?${qs}` : ""}`, { method: "GET" });
  },
  forceCancelCareHubBooking: (bookingId, payload) =>
    request(`/api/ops/carehub/bookings/${encodeURIComponent(bookingId)}/force-cancel`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  forceReassignCareHubBooking: (bookingId, payload) =>
    request(`/api/ops/carehub/bookings/${encodeURIComponent(bookingId)}/force-reassign`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  listCareHubGstInvoices: (params = {}) => {
    const q = new URLSearchParams();
    if (params.limit != null) q.set("limit", String(params.limit));
    const qs = q.toString();
    return request(`/api/ops/carehub/gst-invoices${qs ? `?${qs}` : ""}`, { method: "GET" });
  },
  listCareHubSupportTickets: () => request("/api/ops/carehub/support-tickets", { method: "GET" }),
  replyCareHubSupportTicket: (ticketId, payload) =>
    request(`/api/ops/carehub/support-tickets/${encodeURIComponent(ticketId)}/reply`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  listMyCareHubSupportTickets: () =>
    request("/api/caregiver/carehub/support-tickets", { method: "GET" }),
  createCareHubSupportTicket: (payload) =>
    request("/api/caregiver/carehub/support-tickets", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  readAccessControlAudit,
  fetchOpsAccessControlAudit,
  appendAccessControlAudit,
  readSecurityFlags,
  addSecurityFlag,
  dismissSecurityFlag,

  /** PUT caregiver accepts booking (PENDING → ACCEPTED) */
  acceptBooking: (bookingId) =>
    request(`/api/bookings/${bookingId}/accept`, { method: "PUT" }),

  /** POST caregiver accepts booking (legacy; requires caregiver_id in body) */
  acceptBookingAssign: (bookingId, payload) =>
    request(`/api/bookings/${bookingId}/assign`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST caregiver rejects booking */
  rejectBooking: (bookingId, payload) =>
    request(`/api/bookings/${bookingId}/reject`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST caregiver verifies booking OTP */
  verifyBookingOtp: (bookingId, payload) =>
    request(`/api/bookings/${bookingId}/verify-otp`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** Engagement lifecycle — caregiver journey */
  getEngagementState: (bookingId) =>
    request(`/api/engagements/${encodeURIComponent(bookingId)}/state`, { method: "GET" }),

  startEngagementJourney: (bookingId) =>
    request(`/api/engagements/${encodeURIComponent(bookingId)}/start-journey`, { method: "POST" }),

  markEngagementArrived: (bookingId, payload) =>
    request(`/api/engagements/${encodeURIComponent(bookingId)}/arrived`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  verifyEngagementStartOtp: (bookingId, payload) =>
    request(`/api/engagements/${encodeURIComponent(bookingId)}/verify-start-otp`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  resendEngagementStartOtp: (bookingId) =>
    request(`/api/engagements/${encodeURIComponent(bookingId)}/resend-start-otp`, {
      method: "POST",
    }),

  verifyEngagementEndOtp: (bookingId, payload) =>
    request(`/api/engagements/${encodeURIComponent(bookingId)}/verify-end-otp`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  submitEngagementEarlyEnd: (bookingId, payload) =>
    request(`/api/engagements/${encodeURIComponent(bookingId)}/early-end`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  submitEngagementEmergencyEnd: (bookingId, payload) =>
    request(`/api/engagements/${encodeURIComponent(bookingId)}/emergency-end`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  extendBookingPackage: (bookingId, payload) =>
    request(`/api/bookings/${encodeURIComponent(bookingId)}/extend`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  confirmBookingExtension: (bookingId) =>
    request(`/api/bookings/${encodeURIComponent(bookingId)}/confirm-extension`, { method: "POST" }),

  cancelBookingBeforeStart: (bookingId, payload) =>
    request(`/api/bookings/${encodeURIComponent(bookingId)}/cancel-before-start`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  suspendBooking: (bookingId, payload) =>
    request(`/api/bookings/${encodeURIComponent(bookingId)}/suspend`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  listEngagementIncidents: (bookingId) =>
    request(`/api/engagements/${encodeURIComponent(bookingId)}/incidents`, { method: "GET" }),

  respondTwelveHourContinue: (bookingId, payload) =>
    request(`/api/bookings/${encodeURIComponent(bookingId)}/twelve-hour-continue`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  getEngagementServiceSummary: (bookingId) =>
    request(`/api/engagements/${encodeURIComponent(bookingId)}/service-summary`, { method: "GET" }),

  getEngagementReasonCodes: () => request("/api/engagements/reason-codes", { method: "GET" }),

  getOpsActiveEngagements: () => request("/api/ops/engagements/active", { method: "GET" }),

  /** POST caregiver marks task completed */
  completeBookingTask: (bookingId, taskId) =>
    request(`/api/bookings/${bookingId}/tasks/${taskId}/complete`, {
      method: "POST",
    }),

  /** POST caregiver marks task unable to complete with reason */
  markBookingTaskUnable: (bookingId, taskId, payload) =>
    request(`/api/bookings/${bookingId}/tasks/${taskId}/unable`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** GET live visit clock and overrun indicators */
  getVisitClock: (bookingId) => request(`/api/bookings/${bookingId}/visit-clock`),

  /** POST caregiver requests 30-min extension blocks */
  requestVisitExtension: (bookingId, payload) =>
    request(`/api/bookings/${bookingId}/visit-extension/request`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST customer approves extension request */
  approveVisitExtension: (bookingId) =>
    request(`/api/bookings/${bookingId}/visit-extension/approve`, {
      method: "POST",
    }),

  /** POST customer rejects extension request */
  rejectVisitExtension: (bookingId, payload) =>
    request(`/api/bookings/${bookingId}/visit-extension/reject`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST Ops/Care Manager OTP override with presence confirmation */
  applyOpsOtpOverride: (bookingId, payload) =>
    request(`/api/bookings/${bookingId}/otp-override`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST customer cancels their booking (allowed while PENDING / ACCEPTED / CONFIRMED) */
  cancelBooking: (bookingId, payload) =>
    request(`/api/bookings/${bookingId}/cancel`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /**
   * POST record a real booking edge-case event (cancel request, late arrival,
   * visit overrun, OTP override request). Persisted in `booking_edge_events`
   * server-side; the counter-party + Ops get notified automatically.
   */
  recordBookingEdgeEvent: (bookingId, { eventType, note = "", payload = null } = {}) =>
    request(`/api/bookings/${bookingId}/edge-events`, {
      method: "POST",
      body: JSON.stringify({ eventType, note, payload }),
    }),

  /** GET all edge events for a booking (caregiver / customer / Ops). */
  listBookingEdgeEvents: (bookingId, limit) =>
    request(
      `/api/bookings/${bookingId}/edge-events${limit ? `?limit=${encodeURIComponent(limit)}` : ""}`
    ),

  /** GET all open (unresolved) booking edge events — Ops view. */
  listOpenBookingEdgeEvents: ({ eventType, limit } = {}) => {
    const params = new URLSearchParams();
    if (eventType) params.set("eventType", String(eventType));
    if (limit) params.set("limit", String(limit));
    const qs = params.toString();
    return request(`/api/bookings/edge-events/open${qs ? `?${qs}` : ""}`);
  },

  /** POST resolve an open edge event — Ops / Care Manager. */
  resolveBookingEdgeEvent: (eventId, payload) =>
    request(`/api/bookings/edge-events/${eventId}/resolve`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** GET booking notifications for the signed-in user (customer or caregiver) */
  listMyNotifications: (limit) =>
    request(`/api/notifications${limit ? `?limit=${encodeURIComponent(limit)}` : ""}`),

  /** POST mark a batch of notifications as read */
  markNotificationsRead: (ids) =>
    request(`/api/notifications/mark-read`, {
      method: "POST",
      body: JSON.stringify({ ids: Array.isArray(ids) ? ids : [] }),
    }),

  /** POST mark all notifications as read */
  markAllNotificationsRead: () =>
    request(`/api/notifications/mark-read`, {
      method: "POST",
      body: JSON.stringify({ all: true }),
    }),

  /** GET Firebase web push config (public keys) */
  getPushConfig: () => request("/api/notifications/push-config", { timeoutMs: 15000 }),

  /** POST register FCM device token for push notifications */
  registerDeviceToken: ({ token, platform = "web" }) =>
    request("/api/notifications/device-token", {
      method: "POST",
      body: JSON.stringify({ token, platform }),
      timeoutMs: 15000,
    }),

  /** GET push registration status for signed-in user */
  getPushStatus: () => {
    if (!readAuthToken()) {
      return Promise.resolve(null);
    }
    return request("/api/notifications/push-status", { method: "GET", timeoutMs: 15000 });
  },

  /** POST send a test browser push to the signed-in user */
  sendTestPush: ({ token } = {}) =>
    request("/api/notifications/test-push", {
      method: "POST",
      body: JSON.stringify(token ? { token } : {}),
      timeoutMs: 20000,
    }),

  /** DELETE unregister FCM device token */
  unregisterDeviceToken: ({ token }) =>
    request("/api/notifications/device-token", {
      method: "DELETE",
      body: JSON.stringify({ token }),
    }),

  /* ─── Availability + slot locking ──────────────────────────────────── */

  /** GET available slots for a caregiver on a date */
  listAvailableSlots: ({
    caregiverId,
    date,
    duration,
    serviceRole,
    serviceId,
    lat,
    lng,
    instantOnly,
    excludeBookingId,
  }) => {
    const params = new URLSearchParams({
      caregiverId: String(caregiverId),
      date: String(date),
    });
    if (duration) params.set("duration", String(duration));
    if (serviceRole) params.set("serviceRole", String(serviceRole));
    if (serviceId) params.set("serviceId", String(serviceId));
    if (lat != null) params.set("lat", String(lat));
    if (lng != null) params.set("lng", String(lng));
    if (instantOnly) params.set("instantOnly", "true");
    if (excludeBookingId != null && excludeBookingId !== "") {
      params.set("excludeBookingId", String(excludeBookingId));
    }
    return request(`/api/availability/slots?${params.toString()}`, {
      method: "GET",
      includeAuthIfPresent: true,
    });
  },

  /** POST validate a specific slot (no side effects) — public like slot listing */
  validateAvailabilitySlot: (payload) =>
    request(`/api/availability/validate`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
      includeAuthIfPresent: true,
    }),

  /** POST reserve a slot with a short-lived lock */
  lockAvailabilitySlot: (payload) =>
    request(`/api/availability/lock`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  /** POST release a previously acquired slot lock */
  releaseAvailabilityLock: (token) =>
    request(`/api/availability/release`, {
      method: "POST",
      body: JSON.stringify({ token }),
    }),

  /** POST validate that every occurrence of a recurring booking is bookable */
  validateRecurringAvailability: (payload) =>
    request(`/api/availability/recurring/validate`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  /** GET weekly schedule for any caregiver (admin / care manager view) */
  getCaregiverWeeklySchedule: ({ caregiverId, fromDate, days }) => {
    const params = new URLSearchParams({
      caregiverId: String(caregiverId),
      fromDate: String(fromDate),
    });
    if (days) params.set("days", String(days));
    return request(`/api/availability/weekly?${params.toString()}`);
  },

  /**
   * GET booking calendar availability for a caregiver over a date range.
   * Used by Care Attendant booking date picker to grey out unavailable days.
   */
  getAvailabilityCalendar: ({
    caregiverId,
    fromDate,
    toDate,
    days,
    duration,
    serviceRole,
    serviceId,
    lat,
    lng,
    instantOnly = false,
    excludeBookingId,
  } = {}) => {
    const params = new URLSearchParams({
      caregiverId: String(caregiverId),
      fromDate: String(fromDate),
    });
    if (toDate) params.set("toDate", String(toDate));
    if (days != null) params.set("days", String(days));
    if (duration != null) params.set("duration", String(duration));
    if (serviceRole) params.set("serviceRole", String(serviceRole));
    if (serviceId) params.set("serviceId", String(serviceId));
    if (lat != null) params.set("lat", String(lat));
    if (lng != null) params.set("lng", String(lng));
    if (instantOnly) params.set("instantOnly", "true");
    if (excludeBookingId != null && excludeBookingId !== "") {
      params.set("excludeBookingId", String(excludeBookingId));
    }
    return request(`/api/availability/calendar?${params.toString()}`, {
      method: "GET",
      includeAuthIfPresent: true,
    });
  },

  /* ─── Caregiver self-service schedule + leave ──────────────────────── */

  getMyWorkingHours: () => request(`/api/caregiver/schedule/working-hours`),

  saveMyWorkingHours: (entries) =>
    request(`/api/caregiver/schedule/working-hours`, {
      method: "PUT",
      body: JSON.stringify({ entries: Array.isArray(entries) ? entries : [] }),
    }),

  listMyLeave: ({ fromDate, toDate } = {}) => {
    const params = new URLSearchParams();
    if (fromDate) params.set("fromDate", fromDate);
    if (toDate) params.set("toDate", toDate);
    const qs = params.toString();
    return request(`/api/caregiver/schedule/leave${qs ? `?${qs}` : ""}`);
  },

  addMyLeave: (payload) =>
    request(`/api/caregiver/schedule/leave`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  removeMyLeave: (leaveId) =>
    request(`/api/caregiver/schedule/leave/${encodeURIComponent(leaveId)}`, {
      method: "DELETE",
    }),

  getMyWeeklySchedule: ({ fromDate, days } = {}) => {
    const params = new URLSearchParams();
    if (fromDate) params.set("fromDate", fromDate);
    if (days) params.set("days", String(days));
    const qs = params.toString();
    return request(`/api/caregiver/schedule/weekly${qs ? `?${qs}` : ""}`);
  },

  /** GET /api/caregiver/availability/month?year=&month= */
  getMyAvailabilityMonth: async ({ year, month } = {}) => {
    const params = new URLSearchParams();
    if (year != null) params.set("year", String(year));
    if (month != null) params.set("month", String(month));
    const qs = params.toString();
    const raw = await request(`/api/caregiver/availability/month${qs ? `?${qs}` : ""}`, {
      method: "GET",
    });
    const days = Array.isArray(raw?.days)
      ? raw.days
      : Array.isArray(raw?.data?.days)
        ? raw.data.days
        : [];
    return {
      ...raw,
      days,
      hasCustomerBookings:
        raw?.hasCustomerBookings != null
          ? raw.hasCustomerBookings
          : raw?.data?.hasCustomerBookings != null
            ? raw.data.hasCustomerBookings
            : null,
    };
  },

  getMyEmergencyPause: () => request(`/api/caregiver/schedule/emergency-pause`),

  setMyEmergencyPause: (payload) =>
    request(`/api/caregiver/schedule/emergency-pause`, {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  getMyAvailabilityStatus: () => request(`/api/caregiver/availability-status`),

  setMyAvailabilityStatus: (payload) =>
    request(`/api/caregiver/availability-status`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),

  /** PATCH /api/caregiver/:id/status — self or admin/ops */
  patchCaregiverAvailabilityStatus: (caregiverId, payload) =>
    request(`/api/caregiver/${encodeURIComponent(caregiverId)}/status`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),

  updateMyCareHubSettings: (payload) =>
    request(`/api/caregiver/schedule/carehub-settings`, {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }),

  getCareHubCatalog: () =>
    request(`/api/marketplace/carehub/catalog`, { method: "GET", skipAuth: true }),

  listCareHubMatchmaker: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== "") q.set(k, String(v));
    });
    return request(`/api/availability/matchmaker?${q.toString()}`, {
      method: "GET",
      includeAuthIfPresent: true,
    });
  },

  getCareHubAgreementReference: () =>
    request("/api/marketplace/carehub/agreement-reference", {
      method: "GET",
      includeAuthIfPresent: true,
    }),

  estimateCareHubGuestBooking: (payload) =>
    request(`/api/marketplace/carehub/estimate`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
      // Prefer auth when present so excludeUserId soft-exclusion can apply.
      includeAuthIfPresent: true,
    }),

  confirmCareHubGuestBooking: (payload) =>
    request(`/api/marketplace/carehub/confirm`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  rescheduleBooking: (bookingId, payload) =>
    request(`/api/bookings/${bookingId}/reschedule`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** Customer-initiated reschedule request (requires caregiver approval). */
  getBookingRescheduleEligibility: (bookingId) =>
    request(`/api/bookings/${bookingId}/reschedule/eligibility`, { method: "GET" }),
  getBookingRescheduleDetails: (bookingId) =>
    request(`/api/bookings/${bookingId}/reschedule/details`, { method: "GET" }),
  previewBookingReschedule: (bookingId) =>
    request(`/api/bookings/${bookingId}/reschedule/details`, { method: "GET" }),
  requestBookingReschedule: (bookingId, payload) =>
    request(`/api/bookings/${bookingId}/reschedule-request`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  getBookingRescheduleRequest: (bookingId) =>
    request(`/api/bookings/${bookingId}/reschedule-request`, { method: "GET" }),
  getBookingRescheduleHistory: (bookingId) =>
    request(`/api/bookings/${bookingId}/reschedule/history`, { method: "GET" }),
  getBookingRescheduleSla: (bookingId) =>
    request(`/api/bookings/${bookingId}/reschedule/sla`, { method: "GET" }),
  chooseBookingRescheduleUnavailableAction: (requestId, payload) =>
    request(`/api/bookings/reschedule-requests/${requestId}/unavailable-action`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  requestAdditionalReschedules: (bookingId, payload) =>
    request(`/api/bookings/${bookingId}/reschedule-quota-request`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  getAdditionalRescheduleRequest: (bookingId) =>
    request(`/api/bookings/${bookingId}/reschedule-quota-request`, { method: "GET" }),
  cancelBookingRescheduleRequest: (requestId) =>
    request(`/api/bookings/reschedule-requests/${requestId}/cancel`, { method: "POST" }),
  /** Caregiver: accept/reject a pending reschedule request. */
  acceptBookingReschedule: (requestId) =>
    request(`/api/bookings/reschedule-requests/${requestId}/accept`, { method: "POST" }),
  rejectBookingReschedule: (requestId, payload) =>
    request(`/api/bookings/reschedule-requests/${requestId}/reject`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  listMyPendingRescheduleRequests: () =>
    request(`/api/bookings/reschedule-requests/mine`, { method: "GET" }),

  /** POST /api/ops/verification/:verificationId/approve */
  approveOpsVerification: (verificationId, payload) =>
    request(`/api/ops/verification/${verificationId}/approve`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST /api/ops/verification/:verificationId/reject */
  rejectOpsVerification: (verificationId, payload) =>
    request(`/api/ops/verification/${verificationId}/reject`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  /** POST /api/ops/security-anomalies/:flagId/dismiss */
  dismissOpsAnomaly: (flagId, payload) =>
    request(`/api/ops/security-anomalies/${flagId}/dismiss`, {
      method: "POST",
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  getOpsPremiumQualificationData,
  updateOpsPremiumQualificationStatus,
  fetchCaregiverPremiumCompliance,
  putCaregiverPremiumCompliance,
  getOpsInsuranceWorkflowData,
  updateOpsInsuranceWorkflowStatus,
  getOrCreateDeviceFingerprint,
  recordDeviceLogin,
  getDeviceLoginAudit: readDeviceLoginAudit,
  getDpdpConsent: fetchDpdpConsent,
  saveDpdpConsent: saveDpdpConsentToServer,
  getReplacementCandidates,

  getMessengerConfig: () => request("/api/messenger/config"),
  getMessengerContacts: (params = {}) => {
    const q = new URLSearchParams();
    if (params.q || params.search) q.set("q", String(params.q || params.search).trim());
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return request(`/api/messenger/contacts${qs ? `?${qs}` : ""}`);
  },
  searchMessengerUsers: (q, limit = 50) => {
    const params = new URLSearchParams();
    if (q) params.set("q", String(q).trim());
    if (limit) params.set("limit", String(limit));
    return request(`/api/messenger/users/search?${params.toString()}`);
  },
  prepareMessengerPeer: (userId) =>
    request(`/api/messenger/users/${encodeURIComponent(userId)}/prepare`, { method: "POST" }),
  getMessengerChatToken: () => request("/api/messenger/chat-token"),
  startMessengerCall: (payload) =>
    request("/api/messenger/calls/start", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  bridgeMessengerCall: (sessionId) =>
    request(`/api/messenger/calls/${encodeURIComponent(sessionId)}/bridge`, { method: "POST" }),
  getMessengerIncomingCalls: () => request("/api/messenger/calls/incoming"),
  getMessengerCall: (sessionId) =>
    request(`/api/messenger/calls/${encodeURIComponent(sessionId)}`),
  answerMessengerCall: (sessionId) =>
    request(`/api/messenger/calls/${encodeURIComponent(sessionId)}/answer`, { method: "POST" }),
  endMessengerCall: (sessionId) =>
    request(`/api/messenger/calls/${encodeURIComponent(sessionId)}/end`, { method: "POST" }),
  declineMessengerCall: (sessionId) =>
    request(`/api/messenger/calls/${encodeURIComponent(sessionId)}/decline`, { method: "POST" }),
  notifyMessengerMessage: (payload) =>
    request("/api/messenger/messages/notify", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  recordMessengerActivity: (payload) =>
    request("/api/messenger/messages/activity", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** GET /api/ops/sop/admin-users */
  listAdminUsers: () => request("/api/ops/sop/admin-users"),
  createAdminUser: (payload) =>
    request("/api/ops/sop/admin-users", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),
  setAdminUserStatus: (userId, status) =>
    request(`/api/ops/sop/admin-users/${encodeURIComponent(userId)}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  /** PATCH /api/ops/sop/admin-users/:userId/role */
  setAdminRole: (userId, adminSopRole) =>
    request(`/api/ops/sop/admin-users/${encodeURIComponent(userId)}/role`, {
      method: "PATCH",
      body: JSON.stringify({ adminSopRole }),
    }),

  /** GET /api/ops/sop/admin-access/me */
  getAdminAccess: () => request("/api/ops/sop/admin-access/me"),

  /** GET /api/ops/sop/admin-roles */
  listAdminRoles: (includeInactive = false) =>
    request(`/api/ops/sop/admin-roles${includeInactive ? "?includeInactive=true" : ""}`),

  /** POST /api/ops/sop/admin-roles */
  createAdminRole: (payload) =>
    request("/api/ops/sop/admin-roles", {
      method: "POST",
      body: JSON.stringify(payload || {}),
    }),

  /** PATCH /api/ops/sop/admin-roles/:roleId */
  updateAdminRole: (roleId, payload) =>
    request(`/api/ops/sop/admin-roles/${encodeURIComponent(roleId)}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),

  /** DELETE /api/ops/sop/admin-roles/:roleId */
  deleteAdminRole: (roleId) =>
    request(`/api/ops/sop/admin-roles/${encodeURIComponent(roleId)}`, {
      method: "DELETE",
    }),

  /** POST /api/ops/security/auto-lock-scan */
  runAutoLockScan: () => request("/api/ops/security/auto-lock-scan", { method: "POST" }),

  /** POST /api/ops/security/fraud-scan */
  runFraudScan: () => request("/api/ops/security/fraud-scan", { method: "POST" }),

  /** POST /api/ops/city/liquidity-check */
  triggerCityLiquidityCheck: () => request("/api/ops/city/liquidity-check", { method: "POST" }),

  /** GET /api/ops/city/liquidity-status */
  getCityLiquidityStatus: () => request("/api/ops/city/liquidity-status"),

  /** PATCH /api/ops/city/:city/liquidity-pause */
  setCityLiquidityPause: (city, paused) =>
    request(`/api/ops/city/${encodeURIComponent(city)}/liquidity-pause`, {
      method: "PATCH",
      body: JSON.stringify({ paused }),
    }),

  /** GET /api/ops/settings/onboarding-caps */
  getOnboardingCaps: () => request("/api/ops/settings/onboarding-caps"),

  /** PATCH /api/ops/settings/onboarding-caps */
  patchOnboardingCaps: (payload) =>
    request("/api/ops/settings/onboarding-caps", {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    }),
};

export default api;
